/**
 * Apply All without the server pipeline.
 *
 * The bulk apply panel's apply chain (batching, repair, progress, crediting,
 * comments) is the one write path every Apply All ends in. This module produces
 * the entries that chain writes, in the add-in, from the cards' live previews:
 * nothing is uploaded, forked or polled.
 *
 * CASES, per group of cards
 *   - one card, its live preview present  -> the preview package is the entry, as before
 *   - one card, no live preview yet       -> its redline built over the page's range
 *   - several cards sharing a bookmark    -> folded card by card on a running package
 *     that starts as the page's range, by the same build the live preview uses
 *   - a card over words the user typed, or an earlier card wrote, since it was proposed
 *                                         -> laid over the paragraph as it reads now by
 *     the three-way merge Apply writes with: the card lands as it is drawn, the words it
 *     replaces struck as tracked changes, and no model is asked: single Apply and Apply
 *     All write the same text
 *   - two cards change the same words of a paragraph
 *                                         -> the paragraph is zoned and the zone choice
 *     decides each zone (`chooseConflictOptions`)
 *   - a card in a table cell, or carrying a row or table change
 *                                         -> not folded here: the group's cards are
 *     handed to the panel as `tableCards`, which it writes one by one through
 *     `ProposalApplier`, the one writer that edits a cell paragraph in place and
 *     writes rows and tables through Word's table API
 *
 * WHY table cards never go through the merge writer (2026-09-22)
 *   The bulk writer inserts a package into the bookmarked range. For a cell that
 *   range sits inside a table, and a package that carries the table (a composed
 *   row change, or a cell paragraph read with its table wrapper) is nested by Word
 *   as a copy of the table inside the cell, or refused with GeneralException.
 *
 * WHY one card or one group never fails the run
 *   A card whose own data cannot be laid (its texts do not diff, its redline does not
 *   build over the page) is dropped with the reason and the group folds again
 *   without it. A group whose page cannot be read, or whose entries cannot be
 *   composed, drops its own cards. A core request that fails drops the cards that
 *   needed its answer. Every other card is still written.
 */

import { DocumentComments } from "@common/utils/redline/ooxml/document-comments";
import { OoxmlNumbering } from "@common/utils/redline/ooxml/ooxml-numbering";
import { OoxmlOutlineLevels } from "@common/utils/redline/ooxml/ooxml-outline-levels";
import { ParagraphModel } from "@common/utils/redline/ooxml/paragraph-model";
import { type BookmarkedPages, OoxmlRange } from "@common/utils/redline/ooxml-range";
import type { AcceptedTextSpan, RevertChangeReask, RevertTargetsOfTheRun } from "@common/utils/redline/redline-engine";
import { paragraphIsWrittenWhereGone, redlineWritesNothingWhereItsParagraphsAreGone } from "@common/utils/redline/redline-source";
import { RevertTargetMarks } from "@common/utils/redline/revert-target-marks";
import { NO_TABLE_CHANGES, type TableOperationsApplyResult } from "@common/utils/redline/table-operations";
import {
    cardLandingOnParagraphText,
    type CardChangesOnParagraph,
    type CardParagraphTexts,
    changesOfCardOnText,
    changeWritesOnlySpacing,
    type ChosenZoneText,
    collisionParagraphsOf,
    composeParagraphText,
    type ConflictZone,
    conflictZonesOfParagraph,
    type ParagraphZoning,
    readsAsTheSameWords,
    type RowKeyOfBookmark,
    settledZoneText,
    type UncontestedCardChange,
} from "@common/utils/redline/text/card-collisions";
import { WordTokenizer } from "@common/utils/redline/text/word-tokenizer";
import type { DocumentParagraph } from "@common/utils/redline/wire/document-paragraph";
import { cardKindOfTableChanges, carriesTableOperations, type RedlineTextParagraph, type RedlineTextProposal, type RedlineTextRevert, textsPayloadFrom } from "@common/utils/redline/wire/redline-payload";
import { ON_PARAGRAPH } from "@common/utils/redline/wire/redline-placement";

import { AnchoredProposal, REVERT_REFUSED_USER_MESSAGES } from "./anchor/proposal/anchored-proposal";
import type { LiveReviewBulkApplyComment, LiveReviewBulkApplyRemedy } from "./api";
import type { BulkApplyMergeEntry } from "./bulk-apply-merge-entry";
import type { BulkApplyCommentEntry } from "./document";
import { CANNOT_LOCATE_TARGET_MESSAGE, UserFacingError, userMessageOf, writtenBackReasonOf } from "./errors";
import { type CardCellPresence, cardCellsNotWrittenReasonOrNullOf } from "./redline-cells-not-written";
import { reportError } from "./sentry-reporter";

/**
 * The page's range covering a group's bookmarks, as the fold reads it.
 *
 * WHY the cell flag rides beside the markup
 *   Word Online answers `getOoxml()` on a table-cell paragraph with the paragraph
 *   alone, no `w:tc` or `w:tbl` around it, so `pageOoxml` cannot say the group
 *   writes into a cell. Office.js can, and the routing reads it here.
 *
 * EXAMPLE
 *   empty Annual cell of the Base Rent table, read in Word Online
 *     pageOoxml                              -> one empty <w:p/>, no w:tc, no bookmark
 *     bookmarkedParagraphSitsInTableCell     -> true
 */
export interface BookmarkedPageRead {
    pageOoxml: string;
    /**
     * Whether `pageOoxml` is Word's export as given, its paragraphs not paired with
     * Word's (`BookmarkedRangeRead.wordsExportAsGiven`): the merge writer writes a package
     * built over it as built, since it already ends with Word's paragraph (`mergeEntryOf`).
     *
     * NULL
     *   Absent, or false, on every page that holds the document's paragraphs alone: a page
     *   cut from the whole-document read, a table page read paragraph by paragraph.
     */
    wordsExportAsGiven?: boolean;
    bookmarkedParagraphSitsInTableCell: boolean;
    /**
     * The row each bookmark's cell sits in as Office.js places it
     * (`OfficeDocumentBookmarks.cellRowKeysOf`), for the same reason as the cell flag:
     * Word Online's markup of a cell carries no row. On a table page cut from the
     * whole-document read, the row as that read's markup places it
     * (`BookmarkedTablePageCut`), which holds every table, row and cell.
     *
     * NULL
     *   Absent on a body page cut from the whole-document read, which holds no cell, and
     *   on a page read before Word placed rows: the markup's rows are read (`pageRowPlacementOf`).
     */
    cellRowKeyByBookmarkName?: ReadonlyMap<string, string>;
    /**
     * The listed bookmarks Word no longer finds in the document, on a page read paragraph by
     * paragraph (`readPageParagraphs`): the user deleted the cell, or the row, since the card
     * was written. The run names that cause when it reports the card partly applied
     * without that cell (`partlyAppliedCardsOf`). Empty on a table page cut from the
     * whole-document read, which is cut only when the read holds every listed bookmark.
     *
     * NULL
     *   Absent on a page read as one span or on a body page cut from the whole-document read.
     */
    bookmarkNamesNotFoundInDocument?: ReadonlySet<string>;
}

/**
 * Reads the page's range covering the bookmarks, after finding the cards' bookmarks again as
 * Apply does; null when none of them is in the document any more. `documentPagesOfTheRun` is
 * the run's whole-document read when it is still current (`LocalBulkApplyInput.documentPagesOfTheRun`).
 */
export type PageRangeReader = (bookmarkNames: string[], cards: LiveReviewBulkApplyRemedy[], documentPagesOfTheRun: BookmarkedPages | null) => Promise<BookmarkedPageRead | null>;

/** One card's reading of a collision zone as the core's zone choice reads it: a label for the prompt ("A"), the card's name, the zone's words as the card writes them, and why the card was proposed. */
export interface ConflictZoneOptionRequest {
    option_label: string;
    card_label: string;
    option_text: string;
    intent: string;
    /** True for the reading of a card that strikes the cell's row (`ConflictZoneOption.removesRow`): its text is "", and the core names it as the row's removal. */
    removes_row: boolean;
    /**
     * Always false: every reading the add-in sends is a pending card's.
     *
     * WHY it is still sent
     *   A core that still offers the document's own text as a reading requires the field
     *   on every option and answers 400 without it. The add-in and the core deploy
     *   separately (staging runs both deploys at once), so the add-in keeps sending it
     *   until no running core requires it.
     */
    already_in_document: false;
}

/** One collision zone as the core's zone choice reads it: its label ("Z1"), its words today, and every card's reading. */
export interface ConflictZoneRequest {
    zone_label: string;
    original_text: string;
    options: ConflictZoneOptionRequest[];
}

/** One paragraph for the core's zone choice: its bookmark as the label, the paragraph with each open zone replaced by its mark (`⟦Z1⟧`), and the open zones in reading order. */
export interface ConflictParagraphRequest {
    paragraph_label: string;
    text_with_zone_marks: string;
    zones: ConflictZoneRequest[];
}

/** The core's answer for one zone: the option labels kept, the words the zone will hold, and the sentence a card not kept shows. */
export interface ZoneChoice {
    zone_label: string;
    chosen_option_labels: string[];
    zone_text: string;
    reason: string;
}

/** The core's answers for one paragraph's open zones. */
export interface ParagraphZoneChoices {
    paragraph_label: string;
    zones: ZoneChoice[];
}

/** The core choosing, zone by zone, between the cards that collide on each paragraph of a run, in one call: one answer per paragraph, in order. */
export type ConflictOptionsChooser = (paragraphs: ConflictParagraphRequest[]) => Promise<ParagraphZoneChoices[]>;

export interface LocalBulkApplyInput {
    remedies: LiveReviewBulkApplyRemedy[];
    /**
     * The cards recovery placed beside a paragraph because their own paragraphs are all
     * gone (`DeletedParagraphStep`, or the server): written back as new paragraphs by the
     * applier, never laid on a page. Cards that write back the same paragraph are chosen
     * between first (`zonedGroupOfCardsPlacedBeside`).
     *
     * NULL, or absent
     *   A run with no such card.
     */
    remediesPlacedBesideParagraphs?: LiveReviewBulkApplyRemedy[];
    comments: LiveReviewBulkApplyComment[];
    readPageRange: PageRangeReader;
    /**
     * The whole-document read the run placed its cards on (`ApplyPlacements.documentPages`,
     * read again after a bookmark was re-laid or a card recovered; in the second pass
     * `ServerPlacements.documentPages`), handed to `readPageRange` so the fold cuts every
     * page from it.
     *
     * NULL, or absent
     *   Word threw while reading, or the caller made no read: each page is read from Word,
     *   one at a time (`readPagesFromOneDocumentRead`).
     */
    documentPagesOfTheRun?: BookmarkedPages | null;
    chooseConflictOptions: ConflictOptionsChooser;
    /**
     * The part of each page paragraph a card covers, as Apply reads it
     * (`AnchorTrackerRegistry.coveredSpansOver`): Apply All reads each card over these
     * spans (`groupReadOverCoveredSpans`).
     *
     * NULL, or absent
     *   the bookmarks' extent (`SPANS_OF_A_TRACKER_THAT_HAS_SEEN_NOTHING`)
     */
    coveredSpansOver?: CoveredSpansReader;
}

/** The part of each paragraph of a page the card on these bookmarks covers, one span per paragraph of the page. */
export type CoveredSpansReader = (bookmarkNames: readonly string[], pageOoxml: string) => AcceptedTextSpan[];

/**
 * The spans a tracker that has seen nothing reads (`AnchorTracker.coveredSpansOver`):
 * the bookmarks' extent in each paragraph, the whole paragraph where none of them is,
 * with no remembered neighbour to trim and no inside-paragraph placement to narrow by.
 */
const SPANS_OF_A_TRACKER_THAT_HAS_SEEN_NOTHING: CoveredSpansReader = (bookmarkNames, pageOoxml) =>
    new OoxmlRange(pageOoxml).bookmarkSpans({ bookmarkNames: [...bookmarkNames], neighbourTexts: [] }).map((trimmedSpan) => trimmedSpan.span);

/** The one fold stage the progress panel shows: reading the page, laying the cards and settling where they collide, started once and finished once with how many conflicts were resolved. */
export type LocalFoldStep = "checking_conflicts";

/** Told when each fold stage starts and finishes, with a line for the panel to show under the step. */
export interface LocalFoldReporter {
    stepStarted(step: LocalFoldStep): void;
    stepFinished(step: LocalFoldStep, details: string): void;
}

/** The reporter a caller that does not trace the fold passes: nothing is recorded. */
export const SILENT_FOLD_REPORTER: LocalFoldReporter = { stepStarted: () => undefined, stepFinished: () => undefined };

/**
 * A card the fold dropped, by its rule id, with the sentence its card shows as the
 * reason and the carried cards it lost to, so the card can point the user at the
 * changes that blocked it.
 *
 * EMPTY `conflictingRuleIds` when no carried card shares a paragraph with it: the
 *   range folded nothing, the core answered short, or Word refused a table change.
 */
export interface DroppedCard {
    ruleId: string;
    reason: string;
    conflictingRuleIds: string[];
}

/**
 * A card written in part, or written another way than it was laid: reported partly
 * applied, with the reason naming what went differently.
 *
 * CASES, for `everyWriteLanded`
 *   - true  -> every write of the card landed, and the card only says how: its deleted
 *              paragraph written back as a new paragraph, or a landing audit that could
 *              not find words the writer wrote. The card's outcome is posted applied
 *              (N11: a card whose every write landed never posts a failure).
 *   - false -> something of it was not written: a cell, a row. Posted failed with its
 *              reason (decision (a) on A24).
 *
 * EXAMPLE
 *   card on 11.1, the user deleted 11.1, written back after 10.9  -> everyWriteLanded true
 *   table card over three rows, Word did not return row 3        -> everyWriteLanded false
 */
export interface PartlyAppliedCard extends DroppedCard {
    everyWriteLanded: boolean;
    /**
     * True when the card's deleted paragraph was written back as a new paragraph: the one
     * partly-applied outcome the card and the drawer label "Written back" rather than
     * "Partly applied".
     *
     * WHY apart from `everyWriteLanded`
     *   A card whose landing audit could not find words the writer wrote lands every
     *   write too, and still reads as partly applied; only the written-back card says so.
     *
     * NULL (absent): not written back, as for an entry built before this field.
     */
    writtenBack?: boolean;
}

/** One card's wording of a paragraph: which card, and the paragraph as that card would have it read. */
export interface FailedCardWording {
    ruleId: string;
    ruleName: string;
    textProposed: string;
}

/**
 * One paragraph a dropped card changes: the paragraph as the card read it, the
 * wording of every card that landed on the same words, and the dropped card's own
 * wording.
 *
 * CASES
 *   - the card lost a collision       -> `chosen` holds the card or cards that blocked it
 *   - the card was refused on its own -> `chosen` is empty; only its wording is shown
 */
export interface FailedCardParagraph {
    bookmark: string;
    textBefore: string;
    chosen: FailedCardWording[];
    refused: FailedCardWording;
}

/** A dropped card as the panel shows it: the card, the fold's sentence, and every paragraph it changes. */
export interface FailedCardDetail {
    ruleId: string;
    ruleName: string;
    reason: string;
    paragraphs: FailedCardParagraph[];
}

/** A card paragraph the card changes, on a bookmarked paragraph: the only kind a collision can be shown on. */
interface ChangedBookmarkedCardParagraph {
    bookmark: string;
    textProposed: string;
    textWhenProposed: string;
}

/**
 * The paragraphs a text card changes, by bookmark.
 *
 * CASES
 *   - a markup card (no texts)            -> none
 *   - a paragraph the card leaves as it was -> left out
 *   - a paragraph without a bookmark        -> left out; nothing to match another card's paragraph on
 */
function changedBookmarkedParagraphsOf(remedy: LiveReviewBulkApplyRemedy): ChangedBookmarkedCardParagraph[] {
    return (remedy.target_texts?.paragraphs ?? []).flatMap((cardParagraph) =>
        cardParagraph.bookmark !== null && cardParagraph.textProposed !== cardParagraph.textWhenProposed
            ? [{ bookmark: cardParagraph.bookmark, textProposed: cardParagraph.textProposed, textWhenProposed: cardParagraph.textWhenProposed }]
            : []);
}

/**
 * What the panel shows for every dropped text card of a run: the card's own
 * wording of each paragraph it changes, and on each of those paragraphs the
 * wording of every card it lost to.
 *
 * CASES, per dropped card
 *   - a markup card, no texts                    -> left out; there is no wording to show, the row shows its reason alone
 *   - refused on its own (drift, lost paragraph) -> one detail, every paragraph with an empty `chosen`
 *   - lost a collision                            -> one detail, the blocking cards' wordings under `chosen` on the shared paragraphs
 *
 * EXAMPLE
 *   dropped b (30->45 on PAY) lost to a (30->60 on PAY)
 *   -> [{ ruleId: "b", paragraphs: [{ bookmark: PAY, textBefore: "…thirty (30)…", chosen: [a: "…sixty (60)…"], refused: b: "…forty-five (45)…" }] }]
 */
export function failedCardDetailsOf(remedies: LiveReviewBulkApplyRemedy[], droppedCards: DroppedCard[]): FailedCardDetail[] {
    const remedyOf = new Map(remedies.map((remedy) => [remedy.rule_uuid, remedy]));
    return droppedCards.flatMap((dropped) => {
        const droppedRemedy = remedyOf.get(dropped.ruleId);
        if (droppedRemedy === undefined || droppedRemedy.target_texts === null) return [];
        const winners = dropped.conflictingRuleIds.flatMap((ruleId) => {
            const winner = remedyOf.get(ruleId);
            return winner === undefined ? [] : [winner];
        });
        const paragraphs = changedBookmarkedParagraphsOf(droppedRemedy).map((droppedParagraph): FailedCardParagraph => ({
            bookmark: droppedParagraph.bookmark,
            textBefore: droppedParagraph.textWhenProposed,
            chosen: winners.flatMap((winner) =>
                changedBookmarkedParagraphsOf(winner)
                    .filter((winnerParagraph) => winnerParagraph.bookmark === droppedParagraph.bookmark)
                    .map((winnerParagraph) => ({ ruleId: winner.rule_uuid, ruleName: winner.rule_name, textProposed: winnerParagraph.textProposed }))),
            refused: { ruleId: droppedRemedy.rule_uuid, ruleName: droppedRemedy.rule_name, textProposed: droppedParagraph.textProposed },
        }));
        return [{ ruleId: dropped.ruleId, ruleName: droppedRemedy.rule_name, reason: dropped.reason, paragraphs }];
    });
}

/** A card the panel writes through `ProposalApplier`: it sits in a table cell or carries a row or table change. Rule fields are plural so the panel's step queue reads it like any entry. */
export interface TableCardEntry {
    rule_uuids: string[];
    rule_names: string[];
    target_bookmark_names: string[];
    card: RedlineTextProposal;
    /**
     * The page the fold read the card's group on (`ZonedGroup.pagePackageOoxml`): the applier
     * builds the card over it instead of reconciling it in Word again
     * (`ProposalApplyRequest.pageOfTheRun`).
     *
     * NULL
     *   - a card recovery placed beside a paragraph (`besideParagraphCardOf`): no page holds
     *     its own paragraphs, so the applier reads the neighbour it is written beside.
     *   - a group that lists a body paragraph beside its cells (`pageTheApplierBuildsOver`):
     *     the applier reads the range it writes the card over in Word.
     */
    pageOfTheRun: string | null;
}

/** What the panel's apply chain writes: the merge entries the server's pipeline used to hand it, produced in the add-in, the table cards for the applier, the cards the fold dropped with their reasons, and the cards a zone choice kept nowhere. */
export interface LocalBulkApplyEntries {
    mergeData: BulkApplyMergeEntry[];
    /** The written cards that are partly applied once their write lands, each naming what went differently (`partlyAppliedCardsOf`): a paragraph written back, a cell not written. */
    partlyAppliedCards: PartlyAppliedCard[];
    /** The cards credited with nothing written: their paragraphs are gone and they only deleted them (`alreadyAppliedRuleIdsOf`). */
    alreadyAppliedRuleIds: string[];
    commentData: BulkApplyCommentEntry[];
    tableCards: TableCardEntry[];
    /** The cards placed beside a paragraph, for the applier: one per card, or one composed card per set of cards that write back the same paragraph. */
    besideParagraphCards: TableCardEntry[];
    droppedCards: DroppedCard[];
    /** The cards a zone choice kept nowhere: nothing of them is written and nothing of them failed; the run leaves them as they were. */
    notChosenCards: DroppedCard[];
    /** The cards written with part of them given to another reading by a zone choice: applied, part superseded by `conflictingRuleIds`; never failed. */
    partlySupersededCards: DroppedCard[];
    /** The partly superseded cards as the run wrote them, which the run-end check judges in place of the cards as proposed. */
    cardsAsWritten: CardAsWritten[];
}

/** A card as the run wrote it, by its rule id: what the run-end check judges for that card. */
export interface CardAsWritten {
    ruleId: string;
    card: RedlineTextProposal;
}

/** A run the server pipeline performs, followed through the status file it writes. */
export interface PipelineBulkApplyRun {
    kind: "pipeline";
    statusFileUrl: string;
}

/** A run the add-in folded itself, handed to the panel's apply chain as entries. */
export interface LocalBulkApplyRun {
    kind: "local";
    entries: LocalBulkApplyEntries;
}

/** Where a bulk apply's entries come from: the two have nothing in common, so the panel takes one or the other. */
export type BulkApplyRun = PipelineBulkApplyRun | LocalBulkApplyRun;

/**
 * A card the fold could not lay, and the sentence its card shows: Word's, the
 * core's or the fold's own words for what went wrong.
 */
interface FailedCard {
    remedy: LiveReviewBulkApplyRemedy;
    reason: string;
}

/**
 * A group whose cards folded on the page (packages). Every outcome lists the cards of
 * its group the fold could not lay (`failedCards`); `group` holds the others.
 */
interface FoldedGroup {
    kind: "folded";
    group: LiveReviewBulkApplyRemedy[];
    packageOoxmls: string[];
    /** Whether the page the cards folded on is Word's export as given (`BookmarkedPageRead.wordsExportAsGiven`), so its packages are written as built (`mergeEntryOf`). */
    wordsExportAsGiven: boolean;
    failedCards: FailedCard[];
}

/**
 * A group whose paragraphs are no longer in the document: the page read for its
 * bookmarks came back empty, because the user deleted or cut the paragraph after
 * the cards were written. Every card of the group is dropped with the same sentence
 * a single Apply shows for a lost location.
 */
interface LostGroup {
    kind: "lost";
    group: LiveReviewBulkApplyRemedy[];
    failedCards: FailedCard[];
}

/**
 * A group none of whose cards the fold could lay: its page could not be read, its
 * entries could not be built, or every card's own data failed. `group` is empty;
 * every card is in `failedCards` with its reason.
 */
interface FailedGroup {
    kind: "failed";
    group: LiveReviewBulkApplyRemedy[];
    failedCards: FailedCard[];
}

/**
 * A failure one card's own data caused while its group was folded, carrying the
 * card, so the fold drops that card alone and folds the others again.
 */
class CardFoldError extends Error {
    constructor(readonly remedy: LiveReviewBulkApplyRemedy, readonly failure: unknown) {
        super(userMessageOf(failure));
        this.name = "CardFoldError";
    }
}

/**
 * `compute`, run for one card: whatever it throws is that card's failure
 * (`CardFoldError`).
 *
 * CASES
 *   - it returns                              -> its result
 *   - it throws a card's failure already      -> that failure, unchanged: the inner card is the one to drop
 *   - it throws anything else                 -> the failure of `remedy`
 */
function forCard<Result>(remedy: LiveReviewBulkApplyRemedy, compute: () => Result): Result {
    try {
        return compute();
    } catch (failure) {
        if (failure instanceof CardFoldError) throw failure;
        throw new CardFoldError(remedy, failure);
    }
}

/**
 * The sentence a card shows when the fold could not lay it.
 *
 * EXAMPLE
 *   Error("Word: GeneralException reading _term_one_year")
 *   -> "Apply All could not apply this change: Word: GeneralException reading _term_one_year"
 */
const foldFailureReasonOf = (failure: unknown): string => `Apply All could not apply this change: ${userMessageOf(failure)}`;

/**
 * Sends a failure the fold turned into dropped cards to Sentry, with the cards it
 * failed and the stage it failed in, since the run itself goes on.
 */
function reportFoldFailure(failure: unknown, ruleIds: string[], stage: string): void {
    reportError(failure, { extra: { component: "localBulkApplyEntries", stage, ruleIds } });
}

/** Every card of `group` failed with one failure: reported once, each card with the failure's sentence. */
function failedGroupOf(group: LiveReviewBulkApplyRemedy[], failure: unknown, stage: string): FailedGroup {
    reportFoldFailure(failure, group.map((remedy) => remedy.rule_uuid), stage);
    return { kind: "failed", group: [], failedCards: group.map((remedy) => ({ remedy, reason: foldFailureReasonOf(failure) })) };
}

/**
 * One page paragraph of a zoned group: its bookmark, its accepted text as the page
 * reads it (the text every card's runs are laid on), each card's runs on it, and
 * the runs that land as written beside the zones where cards collide.
 */
interface ZonedParagraph {
    bookmarkName: string;
    /** The text every card's runs are laid on: the page as it reads now, or the text the cards were proposed against when the user deleted the paragraph's words since. */
    baseText: string;
    /** The page's accepted text today, which the composed paragraph is written over. */
    pageText: string;
    cardsChanges: CardChangesOnParagraph[];
    zoning: ParagraphZoning;
}

/**
 * The text all cards naming a page paragraph were proposed against, when they share
 * one.
 *
 * NULL
 *   the cards were proposed against different readings of the paragraph; each is
 *   then laid on the page as it reads now.
 */
function sharedProposalTextOf(cardParagraphs: CardParagraphTexts[]): string | null {
    const [firstParagraph] = cardParagraphs;
    return cardParagraphs.every((cardParagraph) => readsAsTheSameWords(cardParagraph.textWhenProposed, firstParagraph.textWhenProposed)) ? firstParagraph.textWhenProposed : null;
}

/**
 * A group where two cards change the same words of a paragraph (a collision zone,
 * `conflictZonesOfParagraph`): every paragraph any card names, read on the page,
 * so the whole group is composed from the page once the zones are chosen.
 */
interface ZonedGroup {
    kind: "zoned";
    group: LiveReviewBulkApplyRemedy[];
    pagePackageOoxml: string;
    /** Whether `pagePackageOoxml` is Word's export as given (`BookmarkedPageRead.wordsExportAsGiven`), so the package composed over it is written as built (`mergeEntryOf`). */
    wordsExportAsGiven: boolean;
    /** Whether the group's writes land in a table (`groupWritesIntoTable`), decided when the page was read; the writer is chosen on it after the zones are settled. */
    groupWritesIntoTable: boolean;
    /** Where the page read placed its cells (`pageRowPlacementOf`), so the rows a kept deletion takes are found as the zoning found them. */
    rowPlacement: PageRowPlacement;
    zonedParagraphs: ZonedParagraph[];
    failedCards: FailedCard[];
}

type GroupOutcome = FoldedGroup | ZonedGroup | LostGroup | FailedGroup;

/** One change of a card to a paragraph the page carries a bookmark for: the one shape the zoning, the chooser and the composition read. `inRowCardDeletes` marks a cell of a row the card deletes. */
interface BookmarkedParagraphChange {
    bookmark: string;
    textWhenProposed: string;
    textProposed: string;
    inRowCardDeletes: boolean;
}

/**
 * The row a page paragraph sits in, as `tableCellPath` names it: the innermost
 * cell's table and row numbers.
 *
 * EXAMPLE
 *   tableCellPath [{ tableNumber 2, rowNumber 3, … }]   -> "2:3"
 *
 * NULL
 *   the paragraph is outside every table.
 */
function tableRowKeyOf(paragraph: DocumentParagraph): string | null {
    /** The last entry is the cell the paragraph is written in; a nested table's paragraph names its inner cell's row. */
    const innermostCell = paragraph.tableCellPath[paragraph.tableCellPath.length - 1];
    return innermostCell === undefined ? null : `${innermostCell.tableNumber}:${innermostCell.rowNumber}`;
}

/**
 * Where one page read places its cells: the row of the paragraph a bookmark names,
 * and the row of a page paragraph, in one key space for the whole read.
 */
interface PageRowPlacement {
    rowKeyOfBookmark: RowKeyOfBookmark;
    rowKeyOfPageParagraph: (pageParagraph: DocumentParagraph) => string | null;
}

/**
 * The rows of a page read's cells.
 *
 * CASES
 *   - the read placed any of its cells (`BookmarkedPageRead.cellRowKeyByBookmarkName`:
 *     Office.js's rows, or on a table page cut from the whole-document read, that read's)
 *       -> those rows: a bookmark's row as the read placed it, and a page paragraph's by
 *          the first of its bookmarks the read placed
 *   - it placed none
 *       -> the markup's rows (`tableRowKeyOf`) of the page paragraph a bookmark is on
 *
 * WHY Word's rows first
 *   Word Online's markup of a cell carries no `w:tc`, so it places no cell, and a cell
 *   of a struck row read there was never read as struck: an answer written into an
 *   empty cell of a struck row met no strike, landed uncontested, and went with the row
 *   (R31 review, 2026-10-16). Office.js places every cell, an empty one included.
 *
 * WHY one source for the whole read
 *   The two name a row differently ("P-7:1" against "0:1"), so a deletion placed by one
 *   and a cell placed by the other would never meet.
 *
 * NULL, from either function
 *   the source cannot place it: a paragraph outside every table, or one Word did not
 *   place. Such a cell is read struck only where a deletion names it
 *   (`collisionParagraphsOf`), never by a guess.
 *
 * EXAMPLE
 *   Word Online, _row1_cite_cell and _row1_reply_cell both placed "P-7:1"
 *   -> rowKeyOfBookmark("_row1_reply_cell") "P-7:1": the reply cell sits in the row the strike deletes
 */
function pageRowPlacementOf(pageParagraphs: DocumentParagraph[], cellRowKeyByBookmarkName: ReadonlyMap<string, string> | undefined): PageRowPlacement {
    /** WHEN Word placed a cell of the read CONSEQUENCE every row is Word's, a bookmark Word did not place read as placed nowhere. */
    if (cellRowKeyByBookmarkName !== undefined && cellRowKeyByBookmarkName.size > 0) {
        return {
            rowKeyOfBookmark: (bookmark) => cellRowKeyByBookmarkName.get(bookmark) ?? null,
            rowKeyOfPageParagraph: (pageParagraph) => pageParagraph.bookmarkNames.map((bookmarkName) => cellRowKeyByBookmarkName.get(bookmarkName)).find((rowKey) => rowKey !== undefined) ?? null,
        };
    }
    return {
        rowKeyOfBookmark: (bookmark) => {
            const pageParagraph = pageParagraphs.find((candidate) => candidate.bookmarkNames.includes(bookmark));
            return pageParagraph === undefined ? null : tableRowKeyOf(pageParagraph);
        },
        rowKeyOfPageParagraph: tableRowKeyOf,
    };
}

/**
 * The rows the card deletes, as the page read places them (`pageRowPlacementOf`): the
 * row of each cell a deletion names.
 *
 * EXAMPLE
 *   rowDeletions [{ rowOfBookmark: "_premium" }], "_premium" in row 2:3   -> {"2:3"}
 */
function deletedRowKeysOf(card: RedlineTextProposal, rowPlacement: PageRowPlacement): Set<string> {
    return new Set(card.tableChanges.rowDeletions.flatMap((deletion) => {
        const rowKey = rowPlacement.rowKeyOfBookmark(deletion.rowOfBookmark);
        return rowKey === null ? [] : [rowKey];
    }));
}

/**
 * The card's changes as paragraph changes: the wrapper that turns a row operation
 * into cell changes, so a cell is zoned, chosen and composed by the same functions
 * as a paragraph.
 *
 * CASES
 *   - a cell of a row the card deletes: the cell a deletion names, or one the page
 *     shows in such a row                  -> emptied: the row goes with every cell in it
 *   - any other paragraph or cell it lists -> as the card carries it, an edit of a row
 *                                             it keeps as much as of a paragraph
 *   - a page paragraph in a deleted row the
 *     card does not list                   -> emptied, named by the page's own bookmark for it
 *   - a row or table the card adds         -> no paragraph change; the operation rides with the card
 *
 * WHY only the cells of rows the card deletes
 *   A card that merges two rows edits the first and deletes the second, and lists the
 *   cells of both. Read as emptied across every cell it listed, Apply All wrote the
 *   kept row's merged question as nothing and struck it, and the row went with the
 *   other (R27, 2026-10-16: two merges in one table lost both rows each).
 *
 * EXAMPLE
 *   card deletes the row of "_premium"; the row holds "_premium" ("Premium support") and "_fee" ("18%")
 *   -> [{ _premium, "Premium support" -> "" }, { _fee, "18%" -> "" }]
 *
 *   card edits "_alpha_ask" ("Who signed?" -> "Who signed, and which record shows it?") and
 *   deletes the row of "_beta_cite", listing "_beta_ask" too
 *   -> [{ _alpha_ask, "Who signed?" -> "Who signed, and which record shows it?" }, { _beta_cite -> "" }, { _beta_ask -> "" }]
 */
function paragraphChangesOf(card: RedlineTextProposal, pageParagraphs: DocumentParagraph[], rowPlacement: PageRowPlacement): BookmarkedParagraphChange[] {
    const deletedRowKeys = deletedRowKeysOf(card, rowPlacement);
    /** A cell the read cannot place is read emptied only where a deletion names it (`collisionParagraphsOf`). */
    const named = collisionParagraphsOf(card, rowPlacement.rowKeyOfBookmark).flatMap((paragraph): BookmarkedParagraphChange[] =>
        (paragraph.bookmark === null ? [] : [{ bookmark: paragraph.bookmark, textWhenProposed: paragraph.textWhenProposed, textProposed: paragraph.textProposed, inRowCardDeletes: paragraph.inRowCardDeletes === true }]));
    const emptiedCells = pageParagraphs.flatMap((pageParagraph): BookmarkedParagraphChange[] => {
        const rowKey = rowPlacement.rowKeyOfPageParagraph(pageParagraph);
        const pageBookmark = pageParagraph.paragraphBookmarkName ?? pageParagraph.bookmarkNames[0] ?? null;
        const alreadyNamed = named.some((paragraph) => pageParagraph.bookmarkNames.includes(paragraph.bookmark));
        if (rowKey === null || pageBookmark === null || alreadyNamed || !deletedRowKeys.has(rowKey)) return [];
        return [{ bookmark: pageBookmark, textWhenProposed: pageParagraph.allChangesAcceptedText, textProposed: "", inRowCardDeletes: true }];
    });
    return [...named, ...emptiedCells];
}

/** A card of the group with its change to one paragraph, and the bookmark it names that paragraph by. */
interface CardNamingParagraph {
    remedy: LiveReviewBulkApplyRemedy;
    cardParagraph: BookmarkedParagraphChange;
    cardBookmark: string;
}

/**
 * One paragraph the cards name, zoned: each card's runs laid on the paragraph's base
 * text and the paragraph cut into runs that land as written and zones where cards
 * collide.
 *
 * CASES
 *   - the user deleted the paragraph's words since
 *     the cards were proposed (it is struck whole
 *     as a tracked change: `pageText` holds no word,
 *     the cards' text does)                    -> the cards' text is the base, and the page
 *                                                takes no part: a card whose paragraph the
 *                                                user deleted applies, so
 *                                                the deletion is no reading to keep
 *   - otherwise                               -> `pageText` is the base: each card is laid on
 *                                                the paragraph as it reads now by the three-way
 *                                                merge Apply writes with (`changesOfCardOnText`),
 *                                                so a card over words the user typed or an
 *                                                earlier card wrote lands as the card is drawn,
 *                                                and only cards ever meet in a zone
 *
 * EXAMPLE
 *   15.1 "… ninety (90) days’ notice." struck whole by the user; cards "sixty (60)", "thirty (30)"
 *   -> base "… ninety (90) days’ notice.", options sixty and thirty only (live run, 2026-10-18:
 *      the choice was also offered the empty reading)
 *
 * EXAMPLE, the user rewrote the card's word
 *   card "Licensor grants" -> "Provider grants", the page reads "Owner grants"
 *   -> base "Owner grants", the card's run "Owner" -> "Provider": no zone, written as Apply writes it
 */
function zonedParagraphOf(namingCards: CardNamingParagraph[], pageText: string): ZonedParagraph {
    const proposalText = sharedProposalTextOf(namingCards.map(({ cardParagraph }) => cardParagraph));
    const userDeletedParagraphWords = proposalText !== null && WordTokenizer.words(pageText).length === 0 && WordTokenizer.words(proposalText).length > 0;
    /**
     * CASES
     *   - the user deleted its words -> the cards' shared text
     *   - otherwise                  -> the page's text
     */
    const baseText = userDeletedParagraphWords && proposalText !== null ? proposalText : pageText;
    /** Each card's changes carry the spacing that rides along with its words (`changesOfCardOnText`), so the composed paragraph writes it as Apply does. */
    const cardsChanges = namingCards.map(({ remedy, cardParagraph }) => ({ cardId: remedy.rule_uuid, changes: forCard(remedy, () => changesOfCardOnText(cardParagraph, baseText)) }));
    /** The cards that strike this cell's row: against another card's edit of the cell, the whole cell is one zone (`conflictZonesOfParagraph`). */
    const cardIdsStrikingParagraph = new Set(namingCards.flatMap(({ remedy, cardParagraph }) => (cardParagraph.inRowCardDeletes ? [remedy.rule_uuid] : [])));
    return {
        /** The first naming card's bookmark: the one the composed paragraph is laid on by, as every card's bookmark names this same paragraph. */
        bookmarkName: namingCards[0].cardBookmark,
        baseText,
        pageText,
        cardsChanges,
        zoning: conflictZonesOfParagraph(baseText, cardsChanges, cardIdsStrikingParagraph),
    };
}

/** Where a deleted paragraph goes back (after page paragraph `pageIndexBefore`, -1 for the front) and the cards that change it. */
interface DeletedParagraphPlacement {
    pageIndexBefore: number;
    namingCards: CardNamingParagraph[];
}

/**
 * Every paragraph the user deleted that a card of the group still changes, zoned
 * like a page paragraph and keyed by the index of the page paragraph it stood after
 * (-1 for the front), so two cards that write it back in different words are chosen
 * between like any two cards on one paragraph.
 *
 * CASES, per deleted paragraph
 *   - one card names it                      -> zoned with that card alone: written back in its words
 *   - several name it with the same change   -> the zone settles alone: written back once
 *   - several name it with different changes -> zones the conflict choice decides; only the words
 *                                               it keeps are written back
 *
 * WHY the cards' own text is the base and the page takes no part
 *   The page holds nothing of the paragraph. The user's deletion is not offered as a
 *   reading: a card whose paragraph the user deleted applies, and its paragraph goes
 *   back in where it stood.
 *
 * WHERE it stands, when two cards place it differently
 *   After the page paragraph the first card that places it after one names; at the
 *   front only when every card does.
 *
 * EXAMPLE
 *   x [_pay, _term -> "The term is two years."], y [_pay, _term -> "The term is three years."], _term deleted
 *   -> { index of _pay -> [{ _term, base "The term is one year.", zones [{ "two" / "three", options x, y }] }] }
 */
function deletedParagraphsZonedByPageIndexBefore(group: LiveReviewBulkApplyRemedy[], pageParagraphs: DocumentParagraph[]): Map<number, ZonedParagraph[]> {
    const placementByBookmark = new Map<string, DeletedParagraphPlacement>();
    for (const remedy of group) {
        const card = remedy.target_texts;
        /** A markup card names no paragraphs; the zoned path takes only text cards. */
        if (card === null) continue;
        for (const [pageIndexBefore, deletedParagraphs] of forCard(remedy, () => cardParagraphsGoneFromRunning(card, pageParagraphs))) {
            for (const deletedParagraph of deletedParagraphs) {
                /** A deleted paragraph always carries a bookmark: that is how it is known to be gone. */
                if (deletedParagraph.bookmark === null) continue;
                const placement = placementByBookmark.get(deletedParagraph.bookmark) ?? { pageIndexBefore, namingCards: [] };
                /** WHEN an earlier card placed it at the front and this card knows the paragraph before it CONSEQUENCE it goes after that paragraph. */
                if (placement.pageIndexBefore < 0) placement.pageIndexBefore = pageIndexBefore;
                placement.namingCards.push({ remedy, cardParagraph: { ...deletedParagraph, bookmark: deletedParagraph.bookmark, inRowCardDeletes: false }, cardBookmark: deletedParagraph.bookmark });
                placementByBookmark.set(deletedParagraph.bookmark, placement);
            }
        }
    }
    const zonedByPageIndexBefore = new Map<number, ZonedParagraph[]>();
    for (const { pageIndexBefore, namingCards } of placementByBookmark.values()) {
        /** The text the cards were proposed against stands in for the page, so no page reading joins the zones. */
        const proposedAgainstText = sharedProposalTextOf(namingCards.map(({ cardParagraph }) => cardParagraph)) ?? namingCards[0].cardParagraph.textWhenProposed;
        zonedByPageIndexBefore.set(pageIndexBefore, [...(zonedByPageIndexBefore.get(pageIndexBefore) ?? []), zonedParagraphOf(namingCards, proposedAgainstText)]);
    }
    return zonedByPageIndexBefore;
}

/**
 * Every paragraph a card of the group names, in document order: each page paragraph
 * a card names (`zonedParagraphOf` over the page's text), and, where the group is
 * written to paragraphs, each paragraph the user deleted that a card still changes
 * (`deletedParagraphsZonedByPageIndexBefore`) right after the page paragraph it
 * stood after.
 *
 * CASES, for a deleted paragraph
 *   - the group writes paragraphs -> zoned, and written back as its zones settle
 *   - the group writes a table    -> left out: a missing cell is reported, never written back (`partlyAppliedCardsOf`)
 *
 * EXAMPLE
 *   cards 45 and 90 on PAY, card "in writing" on PAY
 *   -> [{ PAY, zoning: uncontested ["in writing"], zones [{ "thirty (30)", options 45, 90 }] }]
 */
function zonedParagraphsOf(group: LiveReviewBulkApplyRemedy[], pageParagraphs: DocumentParagraph[], rowPlacement: PageRowPlacement, groupWritesParagraphs: boolean): ZonedParagraph[] {
    /**
     * Each card's paragraph changes are read once, as that card's own work: a card
     * whose data cannot be read fails alone (`forCard`).
     *
     * CASES
     *   - a text card   -> its changes, by bookmark
     *   - a markup card -> none; the caller takes this path only when every card carries texts
     */
    const paragraphChangesOfCard = new Map(group.map((remedy) => {
        const card = remedy.target_texts;
        return [remedy, card === null ? [] : forCard(remedy, () => paragraphChangesOf(card, pageParagraphs, rowPlacement))] as const;
    }));
    const zonedPageParagraphs = pageParagraphs.map((pageParagraph): ZonedParagraph[] => {
        const namingCards = group.flatMap((remedy): CardNamingParagraph[] => {
            const cardParagraph = paragraphChangesOfCard.get(remedy)?.find((candidate) => pageParagraph.bookmarkNames.includes(candidate.bookmark));
            return cardParagraph === undefined ? [] : [{ remedy, cardParagraph, cardBookmark: cardParagraph.bookmark }];
        });
        /** A page paragraph no card names is left as the page has it. */
        return namingCards.length === 0 ? [] : [zonedParagraphOf(namingCards, pageParagraph.allChangesAcceptedText)];
    });
    /** WHEN the group writes a table CONSEQUENCE no deleted paragraph is zoned: a missing cell is reported, never written back. */
    const deletedZonedByPageIndexBefore = groupWritesParagraphs ? deletedParagraphsZonedByPageIndexBefore(group, pageParagraphs) : new Map<number, ZonedParagraph[]>();
    /**
     * EXAMPLE
     *   page zoned [[PAY], []], deleted { -1 -> [LEAD], 0 -> [TERM] }
     *   -> [LEAD, PAY, TERM]: -1 is the front, and page index 0's deleted paragraphs follow PAY
     */
    return [
        ...(deletedZonedByPageIndexBefore.get(-1) ?? []),
        ...zonedPageParagraphs.flatMap((zonedPageParagraph, pageIndex) => [...zonedPageParagraph, ...(deletedZonedByPageIndexBefore.get(pageIndex) ?? [])]),
    ];
}

/**
 * Every bookmark a card covers: the ones it was sent with, and the bookmark of
 * every paragraph its card lists.
 *
 * WHY the card's paragraphs
 *   A card is sent with the bookmarks the agent named, but its card lists every
 *   paragraph its change takes: every cell of a row it strikes, which the core lists
 *   whichever cell the agent named (`section_with_struck_rows`), and the paragraphs
 *   between the ones named. A card editing one of those paragraphs writes what this
 *   card writes, so the two must fold in one group, where the strike is zoned against
 *   the edit (`paragraphChangesOf`).
 *
 * EXAMPLE
 *   sent with [_premium], strikes its row; the card lists _premium, _basis, _fee
 *   -> [_premium, _premium, _basis, _fee]
 */
const bookmarksCoveredBy = (remedy: LiveReviewBulkApplyRemedy): string[] => [
    ...remedy.target_bookmark_names,
    /** A markup card lists no paragraphs; a card paragraph with no bookmark names no paragraph another card can name. */
    ...(remedy.target_texts?.paragraphs ?? []).flatMap((cardParagraph) => (cardParagraph.bookmark === null ? [] : [cardParagraph.bookmark])),
];

/**
 * Cards that cover a common bookmark (`bookmarksCoveredBy`) redline the same
 * paragraph, so they fold together. A card joins every group a member of which
 * covers one of its bookmarks, and those groups become one, so two groups never
 * write the same paragraph. Order within a group is the cards' order, which is the
 * order they fold in.
 */
export function groupRemediesSharingABookmark(remedies: LiveReviewBulkApplyRemedy[]): LiveReviewBulkApplyRemedy[][] {
    let groups: LiveReviewBulkApplyRemedy[][] = [];
    for (const remedy of remedies) {
        const remedyBookmarks = bookmarksCoveredBy(remedy);
        const sharesABookmarkWith = (group: LiveReviewBulkApplyRemedy[]): boolean =>
            group.some((member) => bookmarksCoveredBy(member).some((bookmarkName) => remedyBookmarks.includes(bookmarkName)));
        const joined = groups.filter(sharesABookmarkWith);
        groups = [...groups.filter((group) => !joined.includes(group)), [...joined.flat(), remedy]];
    }
    return groups;
}

const runningParagraphsOf = (packageOoxml: string): DocumentParagraph[] =>
    new OoxmlRange(packageOoxml).documentParagraphs(DocumentComments.none(), OoxmlNumbering.none(), OoxmlOutlineLevels.none());

/**
 * The card's paragraph laid on each running paragraph, one per running paragraph
 * so the build's alignment is exact.
 *
 * CASES, per running paragraph
 *   - the card names it (one of its bookmarks) -> the card's two texts
 *   - it does not                               -> its own text on both sides, so the build leaves it alone
 */
function cardParagraphsOverRunning(card: RedlineTextProposal, running: DocumentParagraph[]): RedlineTextParagraph[] {
    return running.map((paragraph) =>
        cardParagraphLaidOn(card, paragraph) ?? { bookmark: paragraph.paragraphBookmarkName, textProposed: paragraph.allChangesAcceptedText, textWhenProposed: paragraph.allChangesAcceptedText });
}

/**
 * The card's paragraphs no running paragraph carries (the user deleted them since
 * the card was proposed), each keyed by the index of the running paragraph it
 * stood after: the one the card paragraph before it is laid on.
 *
 * CASES, per card paragraph
 *   - a running paragraph carries its bookmark -> laid there; it is the place the gone paragraphs after it follow
 *   - none does                                -> gone, keyed by the running index of the last card paragraph
 *                                                 laid before it, or -1 when none was (the front)
 *   - it carries no bookmark                   -> neither: it names no paragraph of the page
 *
 * WHY the card's own order
 *   A card's paragraphs are the consecutive paragraphs of its range, so the card
 *   paragraph before a deleted one stood right before it in the document.
 *
 * EXAMPLE
 *   card     [_pay, _term], running [P0 "4. Delivery", P1 carrying _pay]; _term deleted
 *   -> { 1 -> [_term] }: written back right after _pay's paragraph
 *   card     [_term, _pay], the same running paragraphs
 *   -> { -1 -> [_term] }: written back at the front, before _pay's paragraph
 */
function cardParagraphsGoneFromRunning(card: RedlineTextProposal, running: DocumentParagraph[]): Map<number, RedlineTextParagraph[]> {
    const goneByRunningIndexBefore = new Map<number, RedlineTextParagraph[]>();
    let runningIndexOfLastLaid = -1;
    for (const cardParagraph of card.paragraphs) {
        /** WHEN the card paragraph carries no bookmark CONSEQUENCE it names no page paragraph, and is neither laid nor gone. */
        if (cardParagraph.bookmark === null) continue;
        const cardParagraphBookmark = cardParagraph.bookmark;
        const runningIndex = running.findIndex((runningParagraph) => runningParagraph.bookmarkNames.includes(cardParagraphBookmark));
        /**
         * CASES
         *   - a running paragraph carries it (index 0 or more) -> it is the last laid card paragraph so far
         *   - none does (-1)                                    -> gone, after the last laid one
         */
        if (runningIndex >= 0) runningIndexOfLastLaid = runningIndex;
        else goneByRunningIndexBefore.set(runningIndexOfLastLaid, [...(goneByRunningIndexBefore.get(runningIndexOfLastLaid) ?? []), cardParagraph]);
    }
    return goneByRunningIndexBefore;
}

/**
 * The card laid over the running paragraphs (`cardParagraphsOverRunning`), with each
 * card paragraph the user deleted kept in its place among them
 * (`cardParagraphsGoneFromRunning`). The build writes such a paragraph back whole,
 * carrying its bookmark, as one tracked insertion where it stood
 * (`RedlineSource.placedOnPageParagraphs`), or writes nothing for it when the card
 * only deleted it.
 *
 * WHY the gone paragraphs ride in the card
 *   The build is the one writer of every card paragraph: a card applied on its own
 *   and the live preview write a deleted paragraph back through the same build.
 *
 * EXAMPLE
 *   card [_pay -> "…forty-five days…", _term -> "The term is two years."], running [_pay]
 *   -> paragraphs [_pay laid, _term as the card has it]: the build lays _pay and
 *      writes "The term is two years." back after it
 */
function cardOverRunningParagraphs(card: RedlineTextProposal, running: DocumentParagraph[]): AnchoredProposal {
    const laidParagraphs = cardParagraphsOverRunning(card, running);
    const goneByRunningIndexBefore = cardParagraphsGoneFromRunning(card, running);
    /**
     * EXAMPLE
     *   laid [L0, L1], gone { -1 -> [G0], 1 -> [G2] }
     *   -> [G0, L0, L1, G2]: -1 is the front, and running index 1's gone paragraphs follow L1
     */
    const paragraphsInDocumentOrder = [
        ...(goneByRunningIndexBefore.get(-1) ?? []),
        ...laidParagraphs.flatMap((laidParagraph, runningIndex) => [laidParagraph, ...(goneByRunningIndexBefore.get(runningIndex) ?? [])]),
    ];
    return new AnchoredProposal(textsPayloadFrom({ ...card, paragraphs: paragraphsInDocumentOrder }), "asWord");
}

/** The card's paragraphs whose bookmarks a running paragraph carries, in the card's order. */
function cardParagraphsNaming(card: RedlineTextProposal, runningParagraph: DocumentParagraph): RedlineTextParagraph[] {
    return card.paragraphs.filter((cardParagraph) => cardParagraph.bookmark !== null && runningParagraph.bookmarkNames.includes(cardParagraph.bookmark));
}

/**
 * The card paragraph `cardOverRunningParagraphs` lays on a running paragraph.
 *
 * CASES
 *   - one card paragraph's bookmark is on it      -> that paragraph
 *   - several are (paragraphs the user merged
 *     by hand, each keeping its bookmark)         -> one paragraph under the first one's bookmark:
 *                                                    their texts when proposed joined, and their
 *                                                    proposed texts joined, in the card's order
 *
 * WHY joined with nothing between
 *   Word merges two paragraphs by taking the first one's paragraph mark out, so the
 *   merged paragraph reads the first text straight on into the second. Joined the
 *   same way, the card's text when proposed reads as the paragraph does, and one
 *   build lays every half's edits: card #10 laid only its first half on 7.5 and lost
 *   its "royalty-free" change (QA step 6, 2026-10-15).
 *
 * EXAMPLE
 *   card       _a "…in the Deliverables." -> "…in the Work Product."
 *              _b "The licence is royalty-free." -> "The licence is royalty-free and fully paid-up."
 *   paragraph  "…in the Deliverables.The licence is royalty-free."  carrying _a and _b
 *   -> _a "…in the Deliverables.The licence is royalty-free." -> "…in the Work Product.The licence is royalty-free and fully paid-up."
 *
 * NULL
 *   The card names none of the paragraph's bookmarks: the paragraph keeps its own text.
 */
function cardParagraphLaidOn(card: RedlineTextProposal, runningParagraph: DocumentParagraph): RedlineTextParagraph | undefined {
    const namingCardParagraphs = cardParagraphsNaming(card, runningParagraph);
    /**
     * CASES
     *   - none or one card paragraph names it -> that one, or none
     *   - several                             -> laid together
     */
    if (namingCardParagraphs.length <= 1) return namingCardParagraphs[0];
    return {
        ...namingCardParagraphs[0],
        textWhenProposed: namingCardParagraphs.map((cardParagraph) => cardParagraph.textWhenProposed).join(""),
        textProposed: namingCardParagraphs.map((cardParagraph) => cardParagraph.textProposed).join(""),
    };
}

/**
 * True when two cards insert new paragraphs after the same paragraph.
 *
 * WHY
 *   One build writes a card's insertions after one paragraph in the card's order,
 *   but every build writes its insertions straight under that paragraph, so laying
 *   the cards one build each puts the later card's paragraph above the earlier one's.
 *
 * EXAMPLE
 *   B inserts "Clause B." after _pay, C inserts "Clause C." after _pay, ranked B then C
 *     one build per card -> _pay, "Clause C.", "Clause B."
 *     one build for both -> _pay, "Clause B.", "Clause C."
 */
function cardsInsertAfterOneParagraph(insertionCards: RedlineTextProposal[]): boolean {
    const anchorBookmarkNamesInsertedAfter = new Set<string>();
    for (const insertionCard of insertionCards) {
        for (const anchorBookmarkName of new Set(insertionCard.insertions.map((paragraphInsertion) => paragraphInsertion.afterBookmark))) {
            /** WHEN an earlier card already inserts after this paragraph CONSEQUENCE the two cards' new paragraphs share one slot */
            if (anchorBookmarkNamesInsertedAfter.has(anchorBookmarkName)) return true;
            anchorBookmarkNamesInsertedAfter.add(anchorBookmarkName);
        }
    }
    return false;
}

/**
 * Every card's insertions laid on the running package in one build, in card
 * order, every running paragraph left as it reads, so new paragraphs that share a
 * slot land in the order the cards were ranked.
 *
 * EXAMPLE
 *   running _pay "Customer shall pay within forty days."; B inserts "Clause B.", C inserts "Clause C.", both after _pay
 *   -> _pay "Customer shall pay within forty days.", "Clause B.", "Clause C."
 */
function withInsertionsInOneBuild(runningPackage: string, insertionCards: RedlineTextProposal[]): string {
    const insertionsOnly: RedlineTextProposal = { ...insertionCards[0], paragraphs: [], insertions: insertionCards.flatMap((insertionCard) => insertionCard.insertions), tableChanges: NO_TABLE_CHANGES, reverts: [] };
    return cardOverRunningParagraphs(insertionsOnly, runningParagraphsOf(runningPackage))
        .buildOverPackage({ rangeOoxml: runningPackage, placement: ON_PARAGRAPH, stampedAt: new Date().toISOString() }).outcome.packageOoxml;
}


const bookmarkNamesOf = (group: LiveReviewBulkApplyRemedy[]): string[] => Array.from(new Set(group.flatMap((remedy) => remedy.target_bookmark_names)));

/**
 * True when every remedy of a run is a text card, which is what lets the run fold
 * in the add-in: a markup remedy anywhere sends the whole run through the server
 * pipeline. The one predicate both Apply All callers (chat and review) route on.
 */
export const everyRemedyCarriesTexts = (remedies: LiveReviewBulkApplyRemedy[]): boolean => remedies.every((remedy) => remedy.target_texts !== null);

/** True when the card asks for a row or a table, whatever else it changes; the operation rides with the card to the applier. */
const cardAddsRowsOrTables = (card: RedlineTextProposal): boolean => card.tableChanges.rowInsertions.length + card.tableChanges.tableInsertions.length > 0;

/** True when the card deletes a row: its cells read as emptied (`paragraphChangesOf`), and the row goes only when every one of them is chosen emptied. */
const cardDeletesRows = (card: RedlineTextProposal): boolean => card.tableChanges.rowDeletions.length > 0;

/**
 * True when the group's writes land in a table. Such a group is written by the
 * applier, which writes cells one by one and rows and tables through Word's table
 * API; the merge writer cannot write into a cell.
 *
 * CASES
 *   - Word places a bookmarked paragraph in a cell (Office.js)    -> true, whatever the read's markup shows
 *   - a page paragraph's markup sits in a cell (`tableCellPath`)  -> true
 *   - a card asks for a row or a table, or deletes a row          -> true
 *   - otherwise                                                   -> false
 *
 * WHY Office.js as well as the markup
 *   Word Online reads a cell paragraph without its `w:tc`, so `tableCellPath` is
 *   empty for every cell there. The Base Rent cards then went to the merge writer,
 *   which found no paragraph for their bookmarks and wrote nothing.
 *
 * EXAMPLE
 *   empty Annual cell read in Word Online: pageOoxml = <w:p/>, sits in cell = true -> true
 */
const groupWritesIntoTable = (group: LiveReviewBulkApplyRemedy[], pageParagraphs: DocumentParagraph[], bookmarkedParagraphSitsInTableCell: boolean): boolean =>
    bookmarkedParagraphSitsInTableCell
    || pageParagraphs.some((paragraph) => paragraph.tableCellPath.length > 0)
    || group.some((remedy) => {
        const card = remedy.target_texts;
        /** A markup card asks for no row or table; a text card's table changes are read as that card's own work (`forCard`). */
        return card !== null && forCard(remedy, () => cardAddsRowsOrTables(card) || cardDeletesRows(card));
    });

/**
 * One card laid on the running package by the live preview's build: the package after it.
 *
 * WHY the card is laid whole over the running text
 *   The build lays the card by the three-way merge single Apply writes with, so a card
 *   over words the user typed, or an earlier card wrote, lands as it is drawn: the
 *   words it replaces are struck as tracked changes and no model is asked. Two cards
 *   of the group that change the same words were zoned before this fold.
 *
 * WHEN one of the card's paragraphs is gone from the running paragraphs (the user
 * deleted it by hand)
 *   CONSEQUENCE
 *     the card is never refused for it: every paragraph it can lay is laid and
 *     written. A card is a redline the lawyer asked for, and a paragraph already
 *     gone is not a reason to withhold the rest.
 */
function cardLaidOnRunningPackage(cardToLay: RedlineTextProposal, runningPackage: string, revertTargetsOfTheRun: RevertTargetsOfTheRun | undefined): string {
    return cardOverRunningParagraphs(cardToLay, runningParagraphsOf(runningPackage))
        .buildOverPackage({ rangeOoxml: runningPackage, placement: ON_PARAGRAPH, stampedAt: new Date().toISOString(), revertTargetsOfTheRun }).outcome.packageOoxml;
}

/**
 * The insertions of every card of the group laid on the folded package in one
 * build, in card order (`withInsertionsInOneBuild`).
 *
 * CASES, when the build of them all fails
 *   - one card's insertions do not build on their own -> that card's failure (`forCard`):
 *                                                      the group folds again without it
 *   - each card's build on its own                    -> the failure of them all, which fails
 *                                                      the group
 */
function withGroupInsertionsInOneBuild(runningPackage: string, group: LiveReviewBulkApplyRemedy[]): string {
    const insertionCards = group.flatMap((remedy) => (remedy.target_texts === null ? [] : [{ remedy, card: remedy.target_texts }]));
    try {
        return withInsertionsInOneBuild(runningPackage, insertionCards.map(({ card }) => card));
    } catch (failureOfAll) {
        insertionCards.forEach(({ remedy, card }) => forCard(remedy, () => withInsertionsInOneBuild(runningPackage, [card])));
        throw failureOfAll;
    }
}

/**
 * Builds the card alone over the page, for a group that is composed rather than
 * laid card by card: a card whose own redline does not build over the page is
 * dropped before its words go into the paragraph composed for every card.
 */
function buildsAloneOverPage(remedy: LiveReviewBulkApplyRemedy, page: string, pageParagraphs: DocumentParagraph[]): void {
    const card = remedy.target_texts;
    /** A markup card is never composed; the caller takes this path only when every card carries texts. */
    if (card === null) return;
    forCard(remedy, () => cardOverRunningParagraphs(card, pageParagraphs).buildOverPackage({ rangeOoxml: page, placement: ON_PARAGRAPH, stampedAt: new Date().toISOString() }));
}

/**
 * One group folded on the page, card by card, by the live preview's build. Two cards
 * that change the same words are zoned first and chosen zone by zone; every other card
 * is laid over the page as it reads now, as single Apply writes it
 * (`cardLaidOnRunningPackage`).
 *
 * Every card reads the page and folds, its live preview notwithstanding: the
 * preview is a snapshot, and the page as Word reads it now is the only text the
 * fold may lay a card on.
 *
 * Every group reads the page and is zoned the same way, a table cell as a
 * paragraph (`paragraphChangesOf` reads a row deletion as its cells emptied); only
 * the writer differs, chosen by where the changes land (`groupWritesIntoTable`).
 *
 * RETURNS
 *   the packages to write (`[]` when the bookmarks are gone from the document, which
 *   the writer reports as the group's rules failing), or the zoned group, each with
 *   the cards whose own data failed (`failedCards`).
 *
 * CASES, for a card whose own data cannot be laid (`CardFoldError`)
 *   - other cards are left -> the card is dropped with the reason and the others fold
 *                             again from the page, as if it had never been selected
 *   - it was the last one  -> the group failed: every card is dropped with its reason
 *
 * WHY nothing is thrown
 *   Word's failure to read the page, or a failure no one card caused, fails this group
 *   alone, its cards dropped with it, and every other group still folds.
 */
function foldGroupOntoPage(groupPageRead: GroupPageRead): GroupOutcome {
    const { group } = groupPageRead;
    /**
     * CASES
     *   - the page could not be read            -> the group failed, every card with Word's error
     *   - none of the group's bookmarks is left -> the group is lost
     *   - otherwise                             -> folded; a failure no one card caused fails the group
     */
    if (groupPageRead.kind === "unread") return failedGroupOf(group, groupPageRead.failure, "fold_group");
    if (groupPageRead.pageRead === null) return { kind: "lost", group, failedCards: [] };
    try {
        return foldCardsDroppingTheFailing(group, groupPageRead.pageRead);
    } catch (failure) {
        return failedGroupOf(group, failure, "fold_group");
    }
}

/** A group of cards with the page read for its bookmarks: the read, or Word's failure reading it. */
type GroupPageRead = ReadGroupPage | UnreadGroupPage;

/** A group whose page Word read; null when none of its bookmarks is left in the document. */
interface ReadGroupPage {
    kind: "read";
    group: LiveReviewBulkApplyRemedy[];
    pageRead: BookmarkedPageRead | null;
    /** The page's paragraphs, parsed once when it was read (`runningParagraphsOf`); empty when the page is gone. */
    pageParagraphs: DocumentParagraph[];
}

/** A group whose page Word failed to read, or returned in a shape that does not parse. */
interface UnreadGroupPage {
    kind: "unread";
    group: LiveReviewBulkApplyRemedy[];
    failure: unknown;
}

/**
 * Reads the page of a group's bookmarks and parses it, holding a failure of either
 * rather than throwing it, so one group's page never fails the others.
 *
 * CASES
 *   - Word reads the page and it parses -> read, with its paragraphs
 *   - none of the bookmarks is left     -> read, with no page and no paragraphs
 *   - Word fails to read it             -> unread, with Word's failure
 *   - the page does not parse           -> unread, with the parse's failure: every card of
 *                                          the group is dropped with it and every other
 *                                          group is written
 *
 * WHY the page is parsed here
 *   Read later, outside any one group's handling (joining groups that share a paragraph),
 *   a page that did not parse threw out of the whole run and no card was written
 *   (-26's audit T1, 2026-10-18).
 */
async function pageReadOfGroup(group: LiveReviewBulkApplyRemedy[], input: LocalBulkApplyInput): Promise<GroupPageRead> {
    try {
        const pageRead = await input.readPageRange(bookmarkNamesOf(group), group, input.documentPagesOfTheRun ?? null);
        return { kind: "read", group, pageRead, pageParagraphs: pageRead === null ? [] : runningParagraphsOf(pageRead.pageOoxml) };
    } catch (failure) {
        return { kind: "unread", group, failure };
    }
}

/**
 * The groups, with every set of groups whose pages share a paragraph joined into one
 * group read afresh, so each paragraph is written by one entry.
 *
 * WHY
 *   Two cards proposed over two paragraphs the user then merged by hand keep their own
 *   bookmarks, so they fall into two groups, and both groups read the one merged
 *   paragraph. Folded apart, each writes its package over that paragraph from the same
 *   read, and the second write takes the first card's words out again (proved through
 *   `applyBulkMergeData`, 2026-10-15). Joined, the cards fold on one running package.
 *
 * CASES, per pair of groups whose pages were read
 *   - one's page carries a bookmark the other names -> joined, the cards in the order
 *                                                      they were selected
 *   - neither does                                  -> left apart
 *   - a group's page could not be read, or is gone  -> left apart: there is no paragraph to share
 *
 * EXAMPLE
 *   group x [_grant], group y [_scope], both pages the merged 7.5 carrying _grant and _scope
 *   -> one group [x, y], its page read again for [_grant, _scope]
 */
async function groupsJoinedOnSharedParagraphs(groupPageReads: GroupPageRead[], input: LocalBulkApplyInput): Promise<GroupPageRead[]> {
    const pageBookmarksOfGroup = groupPageReads.map((groupPageRead) => new Set(
        /**
         * CASES
         *   - the page was read -> every bookmark its paragraphs carry
         *   - unread, or gone   -> none: such a group shares no paragraph
         */
        groupPageRead.kind === "read"
            ? groupPageRead.pageParagraphs.flatMap((pageParagraph) => pageParagraph.bookmarkNames)
            : []));
    /** Whether Word read the group's page and found a paragraph on it: only such a group has a paragraph to share. */
    const pageWasReadOfGroup = (groupIndex: number): boolean => pageBookmarksOfGroup[groupIndex].size > 0;
    /**
     * CASES
     *   - either group's page was not read, or is gone -> not shared, so a group Word failed
     *                                                     to read never pulls a readable
     *                                                     group into its failure
     *   - one's page carries a bookmark the other names -> shared
     *
     * EXAMPLE
     *   group x [_grant] read as the merged 7.5, group y [_scope, _term] unread
     *   -> x's page carries _scope, but y was not read -> left apart: x is written, y dropped
     */
    const pageSharedBetween = (firstIndex: number, secondIndex: number): boolean =>
        pageWasReadOfGroup(firstIndex) && pageWasReadOfGroup(secondIndex)
        && (bookmarkNamesOf(groupPageReads[secondIndex].group).some((bookmarkName) => pageBookmarksOfGroup[firstIndex].has(bookmarkName))
            || bookmarkNamesOf(groupPageReads[firstIndex].group).some((bookmarkName) => pageBookmarksOfGroup[secondIndex].has(bookmarkName)));
    /** Each group's index points at the first group of the set it was joined to; a group no other joins points at itself. */
    const joinedIntoIndex = groupPageReads.map((_groupPageRead, groupIndex) => groupIndex);
    /**
     * CASES
     *   - the group points at itself -> it is its set's first group
     *   - it points at another       -> that group's set's first, followed on
     *
     * EXAMPLE
     *   joinedIntoIndex [0, 0, 1] -> group 2 points at 1, 1 at 0, 0 at itself -> first of group 2's set is 0
     */
    const firstOfSetOf = (groupIndex: number): number => (joinedIntoIndex[groupIndex] === groupIndex ? groupIndex : firstOfSetOf(joinedIntoIndex[groupIndex]));
    for (let laterIndex = 1; laterIndex < groupPageReads.length; laterIndex += 1) {
        for (let earlierIndex = 0; earlierIndex < laterIndex; earlierIndex += 1) {
            /**
             * WHEN the two groups' pages share a paragraph
             *   CONSEQUENCE
             *     the later group's set joins the earlier group's, so the set keeps the earliest group's place
             */
            if (pageSharedBetween(earlierIndex, laterIndex)) joinedIntoIndex[firstOfSetOf(laterIndex)] = firstOfSetOf(earlierIndex);
        }
    }
    const joinedSets = new Map<number, GroupPageRead[]>();
    groupPageReads.forEach((groupPageRead, groupIndex) => joinedSets.set(firstOfSetOf(groupIndex), [...(joinedSets.get(firstOfSetOf(groupIndex)) ?? []), groupPageRead]));
    return Promise.all([...joinedSets.values()].map((joinedSet) => {
        /** WHEN no other group shares the group's paragraphs CONSEQUENCE its own read stands. */
        if (joinedSet.length === 1) return joinedSet[0];
        /**
         * EXAMPLE
         *   selected [b, c, a], set [a] + [b] -> b at 0, a at 2: 0 - 2 < 0, so b comes first -> [b, a]
         */
        const joinedGroup = joinedSet.flatMap((groupPageRead) => groupPageRead.group).sort((leftRemedy, rightRemedy) => input.remedies.indexOf(leftRemedy) - input.remedies.indexOf(rightRemedy));
        return pageReadOfGroup(joinedGroup, input);
    }));
}

/**
 * The cards folded on the page (`foldCardsOnPage`), a card whose own data cannot be
 * laid dropped with its reason and the others folded again without it.
 *
 * CASES, for a card's failure (`CardFoldError`)
 *   - other cards are left -> the others fold again from the page, as if it had never been selected
 *   - it was the last one  -> the group failed: every card is dropped with its reason
 *
 * THROWS
 *   a failure no one card caused, for the caller to drop every card with.
 */
/**
 * The group with each card read over the part of each paragraph it covers, the spans
 * Apply builds the card over (`AnchorTracker.coveredSpansOver`): the page's words
 * outside a card's span stand on both sides of the card (`remedyReadOverCoveredSpans`),
 * so the fold's zones and its build both leave them as they read.
 *
 * WHY
 *   The fold read each page paragraph whole. After the user merged a card's paragraph
 *   with its neighbour, the neighbour's words read as a change made since the card was
 *   proposed: the card collided with them, the conflict choice was offered "Already in
 *   the document" (the neighbour's sentence and the card's first word) against the
 *   card's word, and keeping the card's word struck the neighbour's sentence. Apply of
 *   the same card left it alone (PC13, 2026-10-19).
 *
 * CASES
 *   - Word did not read the page, or none of its bookmarks is left -> as read: no paragraph to read a card over
 *   - the group may write into a table (`groupWritesIntoTable`)     -> as read: the applier writes it and reads
 *                                                                     each card's spans itself, so a card read
 *                                                                     over them here would write the words
 *                                                                     outside its span twice
 *   - otherwise                                                     -> each card read over its spans
 *
 * EXAMPLE
 *   page "Notices go to the addresses above. Customer shall pay within thirty days of invoice." with
 *   _pay on the second sentence; card _pay "Customer shall pay…" -> "Client shall pay…"
 *   -> card _pay "Notices go to the addresses above. Customer shall pay…" -> "Notices go to the addresses above. Client shall pay…"
 */
function groupReadOverCoveredSpans(groupPageRead: GroupPageRead, coveredSpansOver: CoveredSpansReader): GroupPageRead {
    if (groupPageRead.kind === "unread" || groupPageRead.pageRead === null) return groupPageRead;
    const { group, pageRead, pageParagraphs } = groupPageRead;
    if (groupMayWriteIntoTable(group, pageParagraphs, pageRead)) return groupPageRead;
    return { ...groupPageRead, group: group.map((remedy) => remedyReadOverCoveredSpans(remedy, pageParagraphs, coveredSpansOver(remedy.target_bookmark_names, pageRead.pageOoxml))) };
}

/** A revert whose change the page as first read does not hold, and the sentence its card shows for it. */
interface UnfoundRevert {
    revert: RedlineTextRevert;
    sentence: string;
}

/** A card still written once a change of it was taken out: reported partly applied, and judged as the run wrote it. */
interface PartlyWrittenCard {
    partlyApplied: PartlyAppliedCard;
    asWritten: CardAsWritten;
}

/** A group read on its page, with every card's change whose revert the page holds no change for taken out (`groupWithUnfoundRevertsTakenOut`). */
interface GroupWithUnfoundRevertsTakenOut {
    groupPageRead: GroupPageRead;
    /** The cards left with nothing to write, each with the sentence of its revert. */
    cardsNotWritten: FailedCard[];
    partlyWrittenCards: PartlyWrittenCard[];
}

/**
 * The group with each card's change to a paragraph taken out where the card's revert there
 * names a change the page as first read does not hold (`RevertTargetMarks.layOn`): the card's
 * revert and its text for that paragraph go, and everything else it writes stays.
 *
 * WHY the paragraph's change and not the card, nor the group
 *   One card never blocks a run. A revert that cannot be placed threw out of the composed
 *   card of its paragraph, and every card of the paragraph was dropped with it (§11.2,
 *   stage group_entries, 2026-10-23); a card that wrote one paragraph of three showed as not
 *   applied while its other writes stood (§11.3). The card's text for that paragraph assumes
 *   the revert, so it goes with it: written alone it would bring the words back as new text.
 *
 * CASES, for one card
 *   - every revert of it is placed               -> as it is
 *   - something of it is left once taken out     -> what is left is written, and the card is reported
 *                                                   partly applied, naming the change not taken back
 *   - nothing is left                            -> not written, with the sentence of its revert
 *
 * EXAMPLE
 *   card: §11.2 revert (gone) + §11.3 new words -> §11.3 written; "DXC Legal's deletion ", OR A BREACH OF
 *   SECTION 12" was not taken back: The tracked change this card reverts is no longer in the document."
 */
function groupWithUnfoundRevertsTakenOut(groupPageRead: GroupPageRead): GroupWithUnfoundRevertsTakenOut {
    const nothingTakenOut: GroupWithUnfoundRevertsTakenOut = { groupPageRead, cardsNotWritten: [], partlyWrittenCards: [] };
    /** WHEN Word did not read the page, or none of its bookmarks is left CONSEQUENCE no change can be read on it: the fold reports the group. */
    if (groupPageRead.kind === "unread" || groupPageRead.pageRead === null) return nothingTakenOut;
    const reverts = groupPageRead.group.flatMap((remedy) => remedy.target_texts?.reverts ?? []);
    /** WHEN no card of the group reverts anything CONSEQUENCE nothing to read. */
    if (reverts.length === 0) return nothingTakenOut;
    const unresolvedRevertReasks = unresolvedRevertReasksOnPage(groupPageRead.pageRead.pageOoxml, reverts);
    /** WHEN every revert's change is on the page, or the page could not be read for them CONSEQUENCE every card is folded as it is. */
    if (unresolvedRevertReasks.size === 0) return nothingTakenOut;
    const groupLeft: LiveReviewBulkApplyRemedy[] = [];
    const cardsNotWritten: FailedCard[] = [];
    const partlyWrittenCards: PartlyWrittenCard[] = [];
    for (const remedy of groupPageRead.group) {
        const card = remedy.target_texts;
        const unfoundReverts = (card?.reverts ?? []).flatMap((revert): UnfoundRevert[] => {
            const reask = unresolvedRevertReasks.get(RevertTargetMarks.nameOf(revert));
            /** NULL: the page holds this revert's change. */
            return reask === undefined ? [] : [{ revert, sentence: REVERT_REFUSED_USER_MESSAGES[reask.reason] }];
        });
        /** WHEN the card is markup, or every revert of it is placed CONSEQUENCE it is folded as it is. */
        if (card === null || unfoundReverts.length === 0) {
            groupLeft.push(remedy);
            continue;
        }
        const bookmarksNotWritten = new Set(unfoundReverts.map(({ revert }) => revert.bookmark));
        const cardLeft: RedlineTextProposal = {
            ...card,
            paragraphs: card.paragraphs.filter((cardParagraph) => cardParagraph.bookmark === null || !bookmarksNotWritten.has(cardParagraph.bookmark)),
            reverts: card.reverts.filter((revert) => !bookmarksNotWritten.has(revert.bookmark)),
        };
        /** WHEN nothing of the card is left to write CONSEQUENCE it is not written, with its revert's sentence. */
        if (cardLeft.paragraphs.length === 0 && cardLeft.insertions.length === 0 && !carriesTableOperations(cardLeft.tableChanges)) {
            cardsNotWritten.push({ remedy, reason: foldFailureReasonOf(new UserFacingError(Array.from(new Set(unfoundReverts.map(({ sentence }) => sentence))).join(" "))) });
            continue;
        }
        groupLeft.push({ ...remedy, target_texts: cardLeft });
        partlyWrittenCards.push({
            partlyApplied: { ruleId: remedy.rule_uuid, reason: unfoundReverts.map(({ revert, sentence }) => `${revertedChangeNameOf(revert)} was not taken back: ${sentence}`).join(" "), conflictingRuleIds: [], everyWriteLanded: false },
            asWritten: { ruleId: remedy.rule_uuid, card: cardLeft },
        });
    }
    return { groupPageRead: { ...groupPageRead, group: groupLeft }, cardsNotWritten, partlyWrittenCards };
}

/**
 * The reverts whose change the page as first read does not hold, by name, each with the
 * re-ask that read gave (`RevertTargetMarks.layOn`, on a copy the fold never writes).
 *
 * FALLBACK
 *   A page the reading throws on reads as holding every change: each card is folded as it
 *   is, and the fold drops a card whose revert fails as it is laid (`foldCardsDroppingTheFailing`).
 */
function unresolvedRevertReasksOnPage(pageOoxml: string, reverts: RedlineTextRevert[]): ReadonlyMap<string, RevertChangeReask> {
    try {
        return RevertTargetMarks.layOn(new OoxmlRange(pageOoxml), reverts).targetsOfTheRun.unresolvedRevertReasks;
    } catch {
        return new Map();
    }
}

/**
 * The change a revert takes back, named as the user reads it.
 *
 * EXAMPLE
 *   { kind: "del", author: "DXC Legal", text: ", OR A BREACH OF SECTION 12" } -> DXC Legal's deletion ", OR A BREACH OF SECTION 12"
 */
function revertedChangeNameOf(revert: RedlineTextRevert): string {
    const { kind, author, text } = revert.expectedChange;
    /**
     * CASES
     *   - "del" -> deletion
     *   - "ins" -> insertion
     */
    return `${author}'s ${kind === "del" ? "deletion" : "insertion"} "${text}"`;
}

/**
 * Whether the group's writes may land in a table (`groupWritesIntoTable`).
 *
 * WHEN a card's table changes cannot be read
 *   CONSEQUENCE
 *     true: the group is left as read. The fold drops that card alone (`foldCardsDroppingTheFailing`)
 *     and folds the others as read, which is how it read every card before spans.
 */
function groupMayWriteIntoTable(group: LiveReviewBulkApplyRemedy[], pageParagraphs: DocumentParagraph[], pageRead: BookmarkedPageRead): boolean {
    try {
        return groupWritesIntoTable(group, pageParagraphs, pageRead.bookmarkedParagraphSitsInTableCell);
    } catch {
        return true;
    }
}

/**
 * One card read over the spans it covers on the page: each card paragraph laid on a
 * page paragraph has the page's words before its span put before both its texts, and
 * the page's words after its span put after both.
 *
 * CASES, per card paragraph
 *   - no bookmark, or its paragraph is not on the page (the user deleted it) -> as the card has it
 *   - its span is the whole page paragraph                                 -> as the card has it
 *   - its span is part of the page paragraph                               -> both texts widened by the words outside it
 *
 * EXAMPLE, span { startOffset: 35, endOffset: 84 } of
 *   "Notices go to the addresses above. Customer shall pay within thirty days of invoice."
 *   slice(0, 35) = "Notices go to the addresses above. "   (the words before the span)
 *   slice(84)    = ""                                      (none after: 84 is the paragraph's length)
 *   textWhenProposed "Customer shall pay…" -> "Notices go to the addresses above. Customer shall pay…"
 *   textProposed     "Client shall pay…"   -> "Notices go to the addresses above. Client shall pay…"
 *
 * RETURNS
 *   The remedy itself when no paragraph of its card is widened, so a markup card and a
 *   card whose spans are whole paragraphs reach the fold unchanged.
 */
function remedyReadOverCoveredSpans(remedy: LiveReviewBulkApplyRemedy, pageParagraphs: DocumentParagraph[], coveredSpans: AcceptedTextSpan[]): LiveReviewBulkApplyRemedy {
    const card = remedy.target_texts;
    /** WHEN the remedy carries no text card (a markup card) CONSEQUENCE it reaches the fold as it is. */
    if (card === null) return remedy;
    const cardOverSpans = cardReadOverCoveredSpans(card, pageParagraphs, coveredSpans);
    /** WHEN no paragraph of the card was widened CONSEQUENCE the remedy itself, unchanged. */
    return cardOverSpans === card ? remedy : { ...remedy, target_texts: cardOverSpans };
}

/**
 * One text card read over the spans it covers on its page, the page given as the
 * package it was read in: the same reading as `cardReadOverCoveredSpans`, for a caller
 * that holds the read rather than its paragraphs.
 *
 * WHY single Apply calls it
 *   Single Apply builds the card over the same covered spans Apply All does
 *   (`AnchorTracker.coveredSpansOver`). Built as the agent made it, a card whose words
 *   reach past its bookmark's end marker was confined to the words before it, and
 *   nothing was written, while Apply All, which read the card over its spans first,
 *   wrote it.
 *
 * EXAMPLE
 *   cell read      "Supplier's LIABILITY cap will be $1.00 USD. Redline this paragraph please with the changes by others please "
 *   covered span   { startOffset: 0, endOffset: 43 }   (the end marker after "$1.00 USD.")
 *   -> both of the card's texts gain " Redline this paragraph please with the changes by others please " after them
 */
export function cardReadOverCoveredSpansOfPage(card: RedlineTextProposal, pageOoxml: string, coveredSpans: AcceptedTextSpan[]): RedlineTextProposal {
    return cardReadOverCoveredSpans(card, runningParagraphsOf(pageOoxml), coveredSpans);
}

/**
 * One text card read over the spans it covers on the page: each card paragraph laid on
 * a page paragraph has the page's words before its span put before both its texts, and
 * the page's words after its span put after both (`remedyReadOverCoveredSpans`).
 *
 * RETURNS
 *   The card itself when no paragraph of it is widened.
 */
function cardReadOverCoveredSpans(card: RedlineTextProposal, pageParagraphs: DocumentParagraph[], coveredSpans: AcceptedTextSpan[]): RedlineTextProposal {
    let widenedParagraphCount = 0;
    const paragraphsOverSpans = card.paragraphs.map((cardParagraph) => {
        const cardBookmark = cardParagraph.bookmark;
        /**
         * CASES
         *   - the card paragraph has no bookmark -> -1: it names no page paragraph
         *   - its bookmark is on the page        -> the index of the page paragraph carrying it
         *   - it is not (the user deleted it)    -> -1
         */
        const pageParagraphIndex = cardBookmark === null ? -1 : pageParagraphs.findIndex((pageParagraph) => pageParagraph.bookmarkNames.includes(cardBookmark));
        const coveredSpan = coveredSpans[pageParagraphIndex];
        /** WHEN the card paragraph names no page paragraph, or the reader gave no span for it CONSEQUENCE as the card has it. */
        if (pageParagraphIndex < 0 || coveredSpan === undefined) return cardParagraph;
        const pageText = pageParagraphs[pageParagraphIndex].allChangesAcceptedText;
        const pageWordsBeforeSpan = pageText.slice(0, coveredSpan.startOffset);
        const pageWordsAfterSpan = pageText.slice(coveredSpan.endOffset);
        /** WHEN the span is the whole page paragraph CONSEQUENCE as the card has it. */
        if (pageWordsBeforeSpan === "" && pageWordsAfterSpan === "") return cardParagraph;
        widenedParagraphCount += 1;
        return {
            ...cardParagraph,
            textWhenProposed: `${pageWordsBeforeSpan}${cardParagraph.textWhenProposed}${pageWordsAfterSpan}`,
            textProposed: `${pageWordsBeforeSpan}${cardParagraph.textProposed}${pageWordsAfterSpan}`,
        };
    });
    /**
     * CASES
     *   - no card paragraph was widened -> the card itself
     *   - at least one was              -> the card with its widened paragraphs
     */
    return widenedParagraphCount === 0 ? card : { ...card, paragraphs: paragraphsOverSpans };
}

function foldCardsDroppingTheFailing(group: LiveReviewBulkApplyRemedy[], pageRead: BookmarkedPageRead): GroupOutcome {
    const failedCards: FailedCard[] = [];
    let cardsToFold = group;
    while (cardsToFold.length > 0) {
        try {
            return foldCardsOnPage(cardsToFold, pageRead, failedCards);
        } catch (failure) {
            /** WHEN no one card caused the failure CONSEQUENCE it is the group's, for the caller to drop every card with. */
            if (!(failure instanceof CardFoldError)) throw failure;
            reportFoldFailure(failure.failure, [failure.remedy.rule_uuid], "fold_card");
            failedCards.push({ remedy: failure.remedy, reason: foldFailureReasonOf(failure.failure) });
            cardsToFold = cardsToFold.filter((remedy) => remedy !== failure.remedy);
        }
    }
    return { kind: "failed", group: [], failedCards };
}

/**
 * The cards folded on the page they were read from (`foldGroupOntoPage`).
 *
 * THROWS
 *   a card's failure (`CardFoldError`) when one card's own data cannot be laid, so
 *   the caller drops it and folds the others again.
 */
function foldCardsOnPage(group: LiveReviewBulkApplyRemedy[], pageRead: BookmarkedPageRead, failedCards: FailedCard[]): FoldedGroup | ZonedGroup {
    const page = pageRead.pageOoxml;
    const pageParagraphs = runningParagraphsOf(page);
    const rowPlacement = pageRowPlacementOf(pageParagraphs, pageRead.cellRowKeyByBookmarkName);
    const writesIntoTable = groupWritesIntoTable(group, pageParagraphs, pageRead.bookmarkedParagraphSitsInTableCell);
    const zonedParagraphs = zonedParagraphsOf(group, pageParagraphs, rowPlacement, !writesIntoTable);
    /**
     * WHEN two cards of the group change the same words of any paragraph or cell, or
     *      the group writes into a table
     *   CONSEQUENCE
     *     the group is composed from the page zone by zone (`composedZonedGroupEntries`)
     *     instead of laid card by card, so no card's words land over another's
     *     silently and every collision is chosen once, in reading order. A table
     *     group is composed whether or not it collides: the applier takes the composed
     *     texts, with the row and table operations of the cards that land. A paragraph
     *     group's cards are each built over the page first, so a card whose own
     *     redline does not build is dropped before it is composed.
     */
    if (zonedParagraphs.some((zonedParagraph) => zonedParagraph.zoning.zones.length > 0) || writesIntoTable) {
        if (!writesIntoTable) group.forEach((remedy) => buildsAloneOverPage(remedy, page, pageParagraphs));
        return { kind: "zoned", group, pagePackageOoxml: page, wordsExportAsGiven: pageRead.wordsExportAsGiven === true, groupWritesIntoTable: writesIntoTable, rowPlacement, zonedParagraphs, failedCards };
    }
    /** A markup remedy carries no texts, so it has no insertion to compare; the caller takes this path only when every remedy carries them. */
    const groupCards = group.flatMap((remedy) => (remedy.target_texts === null ? [] : [remedy.target_texts]));
    const insertionsLaidTogether = cardsInsertAfterOneParagraph(groupCards);
    const { markedPage, revertTargetsOfTheRun } = pageWithTheRevertTargetsMarked(page, groupCards);
    let runningPackage = markedPage;
    for (const remedy of group) {
        const card = remedy.target_texts;
        /** A markup remedy has no texts to fold; the caller takes this path only when every remedy carries them. */
        if (card === null) continue;
        /**
         * WHEN two cards of the group insert after one paragraph
         *   CONSEQUENCE each card is laid without its insertions, which land together after the group (`withInsertionsInOneBuild`)
         */
        const cardToLay = insertionsLaidTogether ? { ...card, insertions: [] } : card;
        runningPackage = forCard(remedy, () => cardLaidOnRunningPackage(cardToLay, runningPackage, revertTargetsOfTheRun));
    }
    /**
     * CASES
     *   - two cards insert after one paragraph -> the held insertions laid on the folded package, in card order
     *   - no shared slot                       -> each card's insertions were laid with the card
     */
    const foldedPackage = insertionsLaidTogether ? withGroupInsertionsInOneBuild(runningPackage, group) : runningPackage;
    /** The run's marks end with the fold: the package the merge writer hands Word carries none (`RevertTargetMarks.strippedFrom`). */
    return { kind: "folded", group, packageOoxmls: [RevertTargetMarks.strippedFrom(foldedPackage)], wordsExportAsGiven: pageRead.wordsExportAsGiven === true, failedCards };
}

/** The page the fold lays its cards on, with every revert's change marked, and the marks the cards' builds resolve by. */
interface PageWithRevertTargetsMarked {
    markedPage: string;
    /** NULL when no card of the group reverts anything: the page is laid on as read. */
    revertTargetsOfTheRun: RevertTargetsOfTheRun | undefined;
}

/**
 * The page with the change each card's revert names marked on its own elements, read on the
 * page as first read, before any card is laid (`RevertTargetMarks.layOn`).
 *
 * WHY before any card is laid
 *   A card's change ids number the paragraph it was proposed against. Each card is laid on
 *   the page the cards before it left, and every revert they wrote moved the ids after it:
 *   the Harmful Code card's c2 named Maria's deletion of the Open Source card's "knowingly ",
 *   and both "knowingly " insertions read as the one it named (Sentry WORD-ADDIN-TV,
 *   2026-10-23). Found on the page as first read and marked, each change is found again by
 *   its mark in every card's build.
 *
 * CASES
 *   - no card of the group reverts anything -> the page as read, and no marks
 *   - otherwise                             -> the page with the marks, and the run's reading of them
 */
function pageWithTheRevertTargetsMarked(page: string, groupCards: RedlineTextProposal[]): PageWithRevertTargetsMarked {
    const reverts = groupCards.flatMap((card) => card.reverts);
    /** WHEN no card of the group reverts anything CONSEQUENCE the page as read, with no marks to resolve by. */
    if (reverts.length === 0) return { markedPage: page, revertTargetsOfTheRun: undefined };
    const pageRange = new OoxmlRange(page);
    const laid = RevertTargetMarks.layOn(pageRange, reverts);
    return { markedPage: pageRange.serialize(), revertTargetsOfTheRun: laid.targetsOfTheRun };
}

/**
 * The entry the merge writer writes for a group's cards (`applyBulkMergeData`).
 *
 * WHY each package ends with Word's empty paragraph
 *   The merge writer puts each package over the group's whole paragraphs, as Apply puts
 *   its own. The page the cards were built over holds the document's paragraphs alone,
 *   Word's closing empty paragraph left out of every read (`OoxmlRange.bookmarkedPagesOf`,
 *   `OoxmlRange.readOfWordExport`), so it is put back after them here, as Apply puts it
 *   back (`OoxmlRange.withWordsClosingParagraph`).
 *
 * CASES, for the page the packages were built over
 *   - the document's paragraphs alone                  -> each package with Word's paragraph put back
 *   - Word's export as given, its paragraphs not
 *     paired with Word's (`builtOverWordsExportAsGiven`) -> each package as built: it already ends with
 *                                                           Word's paragraph, and a second one would stay
 *                                                           in the document as an empty line
 *
 * EXAMPLE
 *   packages []                                         -> [], an entry that writes nothing
 *   packages [<w:p>Client shall pay…</w:p><w:sectPr/>]  -> [<w:p>Client shall pay…</w:p><w:p/><w:sectPr/>]
 */
const mergeEntryOf = (bookmarkNames: string[], rules: LiveReviewBulkApplyRemedy[], packageOoxmls: string[], builtOverWordsExportAsGiven: boolean): BulkApplyMergeEntry => ({
    target_bookmark_names: bookmarkNames,
    target_ooxml_after: builtOverWordsExportAsGiven ? packageOoxmls : packageOoxmls.map((packageOoxml) => OoxmlRange.withWordsClosingParagraph(packageOoxml)),
    rule_uuids: rules.map((remedy) => remedy.rule_uuid),
    rule_names: rules.map((remedy) => remedy.rule_name),
});

/** What a card shows when the core's answer held no verdict for its paragraph: the answer was short, which is the core's fault and not the card's, so the one action offered is the one that helps. */
const NO_VERDICT_REASON = "These changes could not be reconciled with each other. Regenerate them.";

/**
 * What one group hands the writer: its merge entries, its table cards for the
 * applier, the cards dropped with the reason each card shows, and the cards a zone
 * choice kept nowhere.
 */
interface GroupEntries {
    mergeEntries: BulkApplyMergeEntry[];
    tableCards: TableCardEntry[];
    droppedCards: DroppedCard[];
    /** The cards the zone choice kept nowhere, or the model declined: never written and never failed; `conflictingRuleIds` names the cards kept in their place. */
    notChosenCards: DroppedCard[];
    partlySupersededCards: DroppedCard[];
    cardsAsWritten: CardAsWritten[];
}

/** The choice outcomes of a group no choice reshaped: no card partly superseded, every card judged as proposed. */
const NO_CARD_RESHAPED_BY_A_CHOICE = { partlySupersededCards: [], cardsAsWritten: [] };

/**
 * The label of the zone at `zoneIndex` among a paragraph's zones, counted from 1
 * so the model and the log read "Z1" for the first.
 *
 * EXAMPLE
 *   zoneIndex 0 -> "Z1"
 */
const zoneLabelAt = (zoneIndex: number): string => `Z${zoneIndex + 1}`;

/**
 * The label of the option at `optionIndex` within a zone: "A" for the first card's
 * reading, "B" for the second, and so on.
 *
 * EXAMPLE
 *   optionIndex 1 -> "B": 65 is "A", so 65 + 1 = 66 is "B"
 */
const optionLabelAt = (optionIndex: number): string => String.fromCharCode(65 + optionIndex);

/** The zone's text when the add-in settles it alone (the same reading from every card, or deletions only), or null when the core is asked. */
const settledTextOf = (zone: ConflictZone, zonedParagraph: ZonedParagraph): string | null => settledZoneText(zone, zonedParagraph.cardsChanges, zonedParagraph.baseText);

/**
 * The paragraph as the core's zone choice reads it: the text with every
 * uncontested run written, every settled zone written as settled, and every open
 * zone replaced by its mark; then the open zones with each card's reading.
 *
 * NULL
 *   every zone of the paragraph is settled: nothing to ask.
 */
function zoneChoiceRequestOf(zonedParagraph: ZonedParagraph, group: LiveReviewBulkApplyRemedy[]): ConflictParagraphRequest | null {
    const { zones, uncontestedChanges } = zonedParagraph.zoning;
    const openZoneIndexes = zones.flatMap((zone, zoneIndex) => (settledTextOf(zone, zonedParagraph) === null ? [zoneIndex] : []));
    if (openZoneIndexes.length === 0) return null;
    const remedyOf = (cardId: string): LiveReviewBulkApplyRemedy | undefined => group.find((remedy) => remedy.rule_uuid === cardId);
    const textWithZoneMarks = composeParagraphText(zonedParagraph.baseText, uncontestedChanges, zones.map((zone, zoneIndex) => ({
        zone,
        chosenText: settledTextOf(zone, zonedParagraph) ?? `⟦${zoneLabelAt(zoneIndex)}⟧`,
    })));
    return {
        paragraph_label: zonedParagraph.bookmarkName,
        text_with_zone_marks: textWithZoneMarks,
        zones: openZoneIndexes.map((zoneIndex) => ({
            zone_label: zoneLabelAt(zoneIndex),
            original_text: zones[zoneIndex].originalText,
            options: zones[zoneIndex].options.map((option, optionIndex) => ({
                option_label: optionLabelAt(optionIndex),
                card_label: remedyOf(option.cardId)?.rule_name ?? option.cardId,
                option_text: option.optionText,
                intent: remedyOf(option.cardId)?.intent ?? "",
                removes_row: option.removesRow === true,
                already_in_document: false,
            })),
        })),
    };
}

/**
 * One zone's outcome for the cards in it: the text it will hold, the cards kept,
 * the sentence a card not kept shows, and whether the zone went unanswered because
 * the request for it failed, which fails the cards not kept instead of leaving them
 * not chosen.
 */
interface ZoneOutcome {
    chosenZoneText: ChosenZoneText;
    keptCardIds: string[];
    reason: string;
    requestFailed: boolean;
}

/**
 * Every zone of the paragraph with the text it will hold and the cards it keeps.
 *
 * CASES, per zone
 *   - settled by the add-in              -> the settled text; every card in it is kept, since
 *                                           each asked for the same words or only deleted
 *   - answered by the core               -> the core's text; the cards whose labels it chose, a
 *                                           chosen strike of the cell's row read as "" (`zoneChoiceKeptOf`)
 *   - asked but not answered (core short) -> the words today; no card kept, NO_VERDICT_REASON
 *   - asked, and the request failed       -> the words today; no card kept, the failure as the reason
 */
function zoneOutcomesOf(zonedParagraph: ZonedParagraph, paragraphChoices: ParagraphZoneChoices | undefined, requestFailureReason: string | null): ZoneOutcome[] {
    return zonedParagraph.zoning.zones.map((zone, zoneIndex) => {
        const settledText = settledTextOf(zone, zonedParagraph);
        if (settledText !== null) return { chosenZoneText: { zone, chosenText: settledText }, keptCardIds: zone.options.map((option) => option.cardId), reason: "", requestFailed: false };
        const zoneChoice = paragraphChoices?.zones.find((candidate) => candidate.zone_label === zoneLabelAt(zoneIndex));
        /**
         * WHEN the core answered short, or the request failed
         *   CONSEQUENCE
         *     the zone keeps the words it holds today. No card is kept.
         */
        if (zoneChoice === undefined) {
            return { chosenZoneText: { zone, chosenText: zone.originalText }, keptCardIds: [], reason: requestFailureReason ?? NO_VERDICT_REASON, requestFailed: requestFailureReason !== null };
        }
        const chosenOptions = zone.options.filter((_option, optionIndex) => zoneChoice.chosen_option_labels.includes(optionLabelAt(optionIndex)));
        return { ...zoneChoiceKeptOf(zone, chosenOptions, zoneChoice.zone_text), reason: zoneChoice.reason, requestFailed: false };
    });
}

/** The text a chosen zone holds and the cards it keeps. */
interface ZoneChoiceKept {
    chosenZoneText: ChosenZoneText;
    keptCardIds: string[];
}

/**
 * The core's choice for a zone as the text the zone holds and the cards it keeps,
 * a reading that strikes the cell's row (`ConflictZoneOption.removesRow`) read as
 * the row's removal.
 *
 * CASES
 *   - every chosen reading strikes the row  -> "", those cards kept: the row goes, and the
 *                                              words the model wrote for the zone are not
 *                                              written, since a removed row holds none
 *   - a strike chosen beside an edit        -> the edit's text, the strike set aside: a row
 *                                              cannot both go and hold an edit, and the core
 *                                              refuses such an answer, so only an older core
 *                                              sends it
 *   - no strike chosen                      -> the core's text and the cards chosen, as for
 *                                              any zone
 *
 * EXAMPLE
 *   options A (card 3, strikes the row), B (card 5, "From the signed order form.")
 *   chosen [A], zone_text "(removes this row from the table)"   -> "", kept [3]
 *   chosen [B], zone_text "From the signed order form."          -> that text, kept [5]
 */
function zoneChoiceKeptOf(zone: ConflictZone, chosenOptions: ConflictZone["options"], zoneText: string): ZoneChoiceKept {
    const chosenEdits = chosenOptions.filter((option) => option.removesRow !== true);
    if (chosenOptions.length > 0 && chosenEdits.length === 0) return { chosenZoneText: { zone, chosenText: "" }, keptCardIds: chosenOptions.map((option) => option.cardId) };
    return { chosenZoneText: { zone, chosenText: zoneText }, keptCardIds: chosenEdits.map((option) => option.cardId) };
}

/**
 * The entries of a zoned group once its zones are chosen: every named paragraph
 * composed from the page (uncontested runs as written, each zone as chosen) and
 * written as one package over the page range, carrying every card that landed
 * something; a card that landed nothing is written nowhere and reported as not
 * chosen, with the zones' reasons and the cards kept in its place.
 *
 * CASES, per card
 *   - kept in a zone, or has a run no other card touches, or adds a paragraph -> carried
 *   - in zones only, kept in none                                            -> not chosen
 *   - in zones only, kept in none, and a zone of it went
 *     unanswered because the request failed                                  -> dropped with the failure
 *
 * WHY not chosen is not failed
 *   A card kept in no zone was never tried against the page: the choice put
 *   another reading there. The run leaves the card as it was, and the tracker's
 *   conflict group says how many of its cards were applied. A failed request chose
 *   nothing, so its cards failed.
 *
 * Whether a carried card landed whole or in part is read off the page after the
 * write (`reconcileCardsOnPage`); this only decides what is written.
 *
 * EXAMPLE
 *   PAY zone "thirty (30)": card 45 kept, card 90 not ("Two terms cannot both stand.")
 *   -> one entry [45] with PAY reading forty-five, nothing for [90];
 *      not chosen [{ 90, "Two terms cannot both stand.", conflicting [45] }]
 */
/** A zoned group after the choices: the cards that land something, the cards kept nowhere, the cards a failed request dropped, and the one card composed from the chosen texts, carrying the row and table operations of the cards that land. */
interface ComposedZonedCard {
    carried: LiveReviewBulkApplyRemedy[];
    notChosenCards: DroppedCard[];
    droppedCards: DroppedCard[];
    partlySupersededCards: DroppedCard[];
    cardsAsWritten: CardAsWritten[];
    composedCard: RedlineTextProposal | null;
}

/**
 * The page the applier builds a table group's card over instead of reading the card's
 * range in Word again (`TableCardEntry.pageOfTheRun`).
 *
 * CASES, for the page paragraphs the group's bookmarks are on (`ZonedGroup.rowPlacement`)
 *   - table cells alone                      -> the page: the cell writer reads and builds each
 *                                               cell itself, and the page only tells the applier
 *                                               whether the card changes words
 *   - body paragraphs alone (a card that adds
 *     a table or a row beside them)           -> the page: the span Word's export of those
 *                                               paragraphs gives (`BookmarkedPages.pageOf`)
 *   - a body paragraph beside a cell          -> null: the applier writes the card as one package
 *                                               over a range that crosses into the table, and the
 *                                               page holds each paragraph alone, never that table
 *   A bookmark on no paragraph of the page, a cell Word did not return, counts for neither.
 *
 * EXAMPLE
 *   group on [_pay (body), _premium_cell (row 1:1)]          -> null
 *   group on [_premium_cell, _uplift_cell]                    -> the page
 *   group on [_premium_cell, _deleted_cell (not on the page)] -> the page
 */
function pageTheApplierBuildsOver(zonedGroup: ZonedGroup, pageParagraphs: DocumentParagraph[]): string | null {
    const bookmarksOnThePage = bookmarkNamesOf(zonedGroup.group).filter((bookmarkName) => pageParagraphs.some((pageParagraph) => pageParagraph.bookmarkNames.includes(bookmarkName)));
    const listsACell = bookmarksOnThePage.some((bookmarkName) => zonedGroup.rowPlacement.rowKeyOfBookmark(bookmarkName) !== null);
    const listsABodyParagraph = bookmarksOnThePage.some((bookmarkName) => zonedGroup.rowPlacement.rowKeyOfBookmark(bookmarkName) === null);
    /**
     * CASES
     *   - a body paragraph beside a cell        -> null
     *   - cells alone, or body paragraphs alone -> the page
     */
    return listsACell && listsABodyParagraph ? null : zonedGroup.pagePackageOoxml;
}

/**
 * The zoned group as its writer takes it, with an empty entry for the cards a
 * failed request dropped, which the writer reports as failing.
 *
 * CASES
 *   - the group writes into a table (`groupWritesIntoTable`) -> the composed card itself, one entry carrying
 *                                                               every card that landed something, for the applier
 *   - otherwise                                              -> the composed card built over the page into one
 *                                                               package, for the merge writer
 */
function composedZonedGroupEntries(zonedGroup: ZonedGroup, choicesOfRun: ParagraphZoneChoices[], requestFailureReason: string | null): GroupEntries {
    const { carried, notChosenCards, droppedCards, partlySupersededCards, cardsAsWritten, composedCard } = composedZonedCardOf(zonedGroup, choicesOfRun, requestFailureReason);
    const droppedRemedies = zonedGroup.group.filter((remedy) => droppedCards.some((dropped) => dropped.ruleId === remedy.rule_uuid));
    /**
     * CASES
     *   - a failed request dropped cards -> one empty entry for them, after the written one, so the
     *                                       writer reports their rules failing
     *   - none was dropped               -> no entry
     */
    const droppedEntries = droppedRemedies.length > 0 ? [mergeEntryOf(bookmarkNamesOf(droppedRemedies), droppedRemedies, [], false)] : [];
    const choiceOutcomes = { droppedCards, notChosenCards, partlySupersededCards, cardsAsWritten };
    if (composedCard === null) return { mergeEntries: droppedEntries, tableCards: [], ...choiceOutcomes };
    const pageParagraphs = runningParagraphsOf(zonedGroup.pagePackageOoxml);
    if (zonedGroup.groupWritesIntoTable) {
        const tableCard: TableCardEntry = {
            rule_uuids: carried.map((remedy) => remedy.rule_uuid),
            rule_names: carried.map((remedy) => remedy.rule_name),
            target_bookmark_names: bookmarkNamesOf(zonedGroup.group),
            card: composedCard,
            pageOfTheRun: pageTheApplierBuildsOver(zonedGroup, pageParagraphs),
        };
        return { mergeEntries: droppedEntries, tableCards: [tableCard], ...choiceOutcomes };
    }
    const packageOoxml = cardOverRunningParagraphs(composedCard, pageParagraphs)
        .buildOverPackage({ rangeOoxml: zonedGroup.pagePackageOoxml, placement: ON_PARAGRAPH, stampedAt: new Date().toISOString() }).outcome.packageOoxml;
    return { mergeEntries: [mergeEntryOf(bookmarkNamesOf(zonedGroup.group), carried, [packageOoxml], zonedGroup.wordsExportAsGiven), ...droppedEntries], tableCards: [], ...choiceOutcomes };
}

/** One zoned paragraph with its zones' outcomes and the uncontested runs that still land, once row deletions kept nowhere are set aside. */
interface ZonedParagraphOutcomes {
    zonedParagraph: ZonedParagraph;
    zoneOutcomes: ZoneOutcome[];
    uncontestedChanges: UncontestedCardChange[];
}

/**
 * The row deletions kept whole: a deletion is kept when every zone it is in keeps
 * it, and a deletion no zone contests is kept as it stands. A row cannot be both
 * deleted and edited, so a deletion kept in one cell and not in another is kept
 * nowhere.
 *
 * EXAMPLE
 *   delete-row in zones on "_premium" (kept) and "_fee" (not kept)   -> not kept
 *   delete-row in a zone on "_premium" (kept), "_fee" uncontested     -> kept
 */
function rowDeletionsKeptWhole(zoneOutcomes: ZoneOutcome[], rowDeletionIds: string[]): Set<string> {
    return new Set(rowDeletionIds.filter((ruleId) => zoneOutcomes
        .filter((outcome) => outcome.chosenZoneText.zone.options.some((option) => option.cardId === ruleId))
        .every((outcome) => outcome.keptCardIds.includes(ruleId))));
}

/**
 * The zone's outcome once a row deletion kept in this zone but kept nowhere as a
 * row is set aside.
 *
 * CASES
 *   - no such deletion in the zone                         -> the outcome as it was
 *   - other cards kept beside it (a deletions-only zone)   -> those cards' settled text, those cards kept
 *   - it was the only card kept                            -> the zone's words today, no card kept
 */
function withoutRowDeletionsKeptNowhere(outcome: ZoneOutcome, zonedParagraph: ZonedParagraph, isRowDeletionKeptNowhere: (ruleId: string) => boolean): ZoneOutcome {
    if (!outcome.keptCardIds.some(isRowDeletionKeptNowhere)) return outcome;
    const keptCardIds = outcome.keptCardIds.filter((ruleId) => !isRowDeletionKeptNowhere(ruleId));
    const { zone } = outcome.chosenZoneText;
    if (keptCardIds.length === 0) return { chosenZoneText: { zone, chosenText: zone.originalText }, keptCardIds, reason: outcome.reason, requestFailed: outcome.requestFailed };
    const keptCardsChanges = zonedParagraph.cardsChanges.filter((cardChanges) => keptCardIds.includes(cardChanges.cardId));
    return { chosenZoneText: { zone, chosenText: settledZoneText(zone, keptCardsChanges, zonedParagraph.baseText) ?? zone.originalText }, keptCardIds, reason: outcome.reason, requestFailed: outcome.requestFailed };
}

/**
 * The row and table operations the applier writes with the composed card: every
 * row or table a carried card adds, and the rows of the deletions kept whole.
 */
function tableChangesOf(carried: LiveReviewBulkApplyRemedy[], keptRowDeletionIds: Set<string>): TableOperationsApplyResult {
    const carriedCards = carried.flatMap((remedy) => (remedy.target_texts === null ? [] : [{ ruleId: remedy.rule_uuid, card: remedy.target_texts }]));
    return {
        rowInsertions: carriedCards.flatMap(({ card }) => card.tableChanges.rowInsertions),
        rowDeletions: carriedCards.filter(({ ruleId }) => keptRowDeletionIds.has(ruleId)).flatMap(({ card }) => card.tableChanges.rowDeletions),
        tableInsertions: carriedCards.flatMap(({ card }) => card.tableChanges.tableInsertions),
    };
}

/**
 * The composition of a zoned group once its zones are chosen, the same for a
 * paragraph group and a table group.
 *
 * CASES, per card
 *   - kept in a zone, or has a run no other card touches, or adds a paragraph,
 *     a row or a table                                                -> carried
 *   - deletes a row                                                   -> carried when kept whole (`rowDeletionsKeptWhole`)
 *   - in zones only, kept in none                                     -> not chosen
 *   - in zones only, kept in none, a zone of it unanswered because
 *     the request failed                                              -> dropped with the failure
 *
 * CASES, per paragraph
 *   - its row goes with a deletion kept whole   -> not written: the row operation takes it
 *   - otherwise                                  -> composed from the base text, the uncontested runs and the chosen zones
 *
 * EXAMPLE
 *   "_fee" zone "18%": card 15% kept, delete-row not kept ("The fee stays; the tier is kept.")
 *   -> composed "_fee" reading 15%, carried [15%]; not chosen [{ delete-row, that reason, conflicting [15%] }]
 */
function composedZonedCardOf(zonedGroup: ZonedGroup, choicesOfRun: ParagraphZoneChoices[], requestFailureReason: string | null): ComposedZonedCard {
    const pageParagraphs = runningParagraphsOf(zonedGroup.pagePackageOoxml);
    const rowDeletionIds = zonedGroup.group.flatMap((remedy) => (remedy.target_texts !== null && cardDeletesRows(remedy.target_texts) ? [remedy.rule_uuid] : []));
    const chosenOutcomes = zonedGroup.zonedParagraphs.map((zonedParagraph) => ({
        zonedParagraph,
        zoneOutcomes: zoneOutcomesOf(zonedParagraph, choicesOfRun.find((paragraphChoices) => paragraphChoices.paragraph_label === zonedParagraph.bookmarkName), requestFailureReason),
    }));
    const keptRowDeletionIds = rowDeletionsKeptWhole(chosenOutcomes.flatMap(({ zoneOutcomes }) => zoneOutcomes), rowDeletionIds);
    const isRowDeletionKeptNowhere = (ruleId: string): boolean => rowDeletionIds.includes(ruleId) && !keptRowDeletionIds.has(ruleId);
    const outcomesByParagraph: ZonedParagraphOutcomes[] = chosenOutcomes.map(({ zonedParagraph, zoneOutcomes }) => ({
        zonedParagraph,
        zoneOutcomes: zoneOutcomes.map((outcome) => withoutRowDeletionsKeptNowhere(outcome, zonedParagraph, isRowDeletionKeptNowhere)),
        uncontestedChanges: zonedParagraph.zoning.uncontestedChanges.filter((uncontested) => !isRowDeletionKeptNowhere(uncontested.cardId)),
    }));
    const allZoneOutcomes = outcomesByParagraph.flatMap(({ zoneOutcomes }) => zoneOutcomes);
    const cardIsInAZone = (remedy: LiveReviewBulkApplyRemedy): boolean => allZoneOutcomes.some((outcome) => outcome.chosenZoneText.zone.options.some((option) => option.cardId === remedy.rule_uuid));
    /**
     * CASES, for one of the card's uncontested changes
     *   - it changes words                              -> written
     *   - it changes only spacing, the card in no zone  -> written: it is the card's whole change
     *   - it changes only spacing, the card in a zone   -> written only with the card's words
     *                                                     (`composeParagraphText`), so it lands
     *                                                     nothing of its own
     */
    const writesUncontestedChange = (remedy: LiveReviewBulkApplyRemedy, uncontested: UncontestedCardChange): boolean =>
        uncontested.cardId === remedy.rule_uuid && (!changeWritesOnlySpacing(uncontested.change) || !cardIsInAZone(remedy));
    const cardLandsSomething = (remedy: LiveReviewBulkApplyRemedy): boolean => {
        if (rowDeletionIds.includes(remedy.rule_uuid)) return keptRowDeletionIds.has(remedy.rule_uuid);
        return allZoneOutcomes.some((outcome) => outcome.keptCardIds.includes(remedy.rule_uuid))
            || outcomesByParagraph.some(({ uncontestedChanges }) => uncontestedChanges.some((uncontested) => writesUncontestedChange(remedy, uncontested)))
            || (remedy.target_texts?.insertions.length ?? 0) > 0
            || (remedy.target_texts !== null && cardAddsRowsOrTables(remedy.target_texts));
    };
    const carried = zonedGroup.group.filter(cardLandsSomething);
    const zonesOfCard = (remedy: LiveReviewBulkApplyRemedy): ZoneOutcome[] => allZoneOutcomes.filter((outcome) => outcome.chosenZoneText.zone.options.some((option) => option.cardId === remedy.rule_uuid));
    /** A card kept nowhere because a zone of it went unanswered when the request failed: it failed, nothing chose against it. */
    const lostToFailedRequest = (remedy: LiveReviewBulkApplyRemedy): boolean => zonesOfCard(remedy).some((outcome) => outcome.requestFailed);
    const keptNowhere = zonedGroup.group.filter((remedy) => !cardLandsSomething(remedy));
    const notChosenCards = keptNowhere.filter((remedy) => !lostToFailedRequest(remedy)).map((remedy) => {
        const zonesOfThisCard = zonesOfCard(remedy);
        return {
            ruleId: remedy.rule_uuid,
            reason: Array.from(new Set(zonesOfThisCard.map((outcome) => outcome.reason).filter((reason) => reason.length > 0))).join(" ") || NO_VERDICT_REASON,
            /** The page's own reading is no card to link to; a card that lost to it names no conflicting card. */
            conflictingRuleIds: Array.from(new Set(zonesOfThisCard.flatMap((outcome) => outcome.keptCardIds))),
        };
    });
    const droppedCards = keptNowhere.filter(lostToFailedRequest).map((remedy) => ({
        ruleId: remedy.rule_uuid,
        reason: zonesOfCard(remedy).find((outcome) => outcome.requestFailed)?.reason ?? NO_VERDICT_REASON,
        conflictingRuleIds: [],
    }));
    const firstCard = carried.find((remedy) => remedy.target_texts !== null)?.target_texts ?? null;
    /** A group whose every card was kept nowhere writes nothing; the not-chosen and dropped lists report them all. */
    if (firstCard === null) return { carried, notChosenCards, droppedCards, partlySupersededCards: [], cardsAsWritten: [], composedCard: null };
    const deletedRowKeys = new Set(carried.flatMap((remedy) =>
        (remedy.target_texts !== null && keptRowDeletionIds.has(remedy.rule_uuid) ? Array.from(deletedRowKeysOf(remedy.target_texts, zonedGroup.rowPlacement)) : [])));
    const rowKeyOfZonedParagraph = (zonedParagraph: ZonedParagraph): string | null => {
        const pageParagraph = pageParagraphs.find((candidate) => candidate.bookmarkNames.includes(zonedParagraph.bookmarkName));
        return pageParagraph === undefined ? null : zonedGroup.rowPlacement.rowKeyOfPageParagraph(pageParagraph);
    };
    const composedParagraphs = outcomesByParagraph.map(({ zonedParagraph, zoneOutcomes, uncontestedChanges }) => {
        const rowKey = rowKeyOfZonedParagraph(zonedParagraph);
        /** A cell of a row that goes is left as it reads: the row operation takes it, and the applier skips a cell that does not change. */
        const rowGoes = rowKey !== null && deletedRowKeys.has(rowKey);
        return {
            bookmark: zonedParagraph.bookmarkName,
            /** Written over the page as it reads now; the composition itself was laid out on `baseText`, which carries the page's own changes as runs. */
            textWhenProposed: zonedParagraph.pageText,
            textProposed: rowGoes ? zonedParagraph.pageText : composeParagraphText(zonedParagraph.baseText, uncontestedChanges, zoneOutcomes.map((outcome) => outcome.chosenZoneText)),
        };
    });
    const tableChanges = tableChangesOf(carried, keptRowDeletionIds);
    const composedCard: RedlineTextProposal = {
        ...firstCard,
        /** In document order, each paragraph the user deleted among them, composed like any other (`zonedParagraphsOf`). */
        paragraphs: composedParagraphs,
        insertions: carried.flatMap((remedy) => remedy.target_texts?.insertions ?? []),
        tableChanges,
        cardKind: cardKindOfTableChanges(tableChanges),
        reverts: revertsOfCarriedCards(carried, outcomesByParagraph),
    };
    const partlySupersededCards = carried.flatMap((remedy) => partSupersessionOf(remedy, zonesOfCard(remedy)));
    const cardsAsWritten = partlySupersededCards.map((partlySuperseded): CardAsWritten => {
        const remedy = carried.find((candidate) => candidate.rule_uuid === partlySuperseded.ruleId);
        return { ruleId: partlySuperseded.ruleId, card: cardAsWrittenFor(remedy, composedCard, keptRowDeletionIds) };
    });
    return { carried, notChosenCards, droppedCards, partlySupersededCards, cardsAsWritten, composedCard };
}

/**
 * The pending changes the composed card reverts: every carried card's whose zones on the
 * reverted paragraph kept that card, in the group's order, each change once.
 *
 * WHY only where the card's zones kept it
 *   A revert card is carried for any change of it that lands. When the choice gave the
 *   zone holding its restored words to another card, the revert still took the change
 *   back, and the composed text struck the same words again as a new deletion dated now:
 *   the document's history was rewritten for words the choice rejected (-38 on 71ac81067,
 *   2026-10-18). A zone of the card on that paragraph that it lost may hold the restored
 *   words, so any such zone leaves the revert out, and the change stays as it was.
 *
 * WHY every carried card's
 *   The composed card spread the first carried card, so it carried that card's reverts
 *   alone. A revert card composed with others lost its revert: its restored words were
 *   written as new text beside the deletion it meant to take back (N26, P5, 2026-10-18).
 *   Every table group is zoned, so a revert card in a table always lost it.
 *
 * WHY each change once
 *   Two cards can revert the same change. The builder applies reverts in order, and the
 *   second would find the change already taken back and re-ask the whole composed card.
 *
 * EXAMPLE
 *   carried forty-five [no reverts], revert card [un-strike " of invoice" on _invoice_pay_days]
 *   -> [un-strike " of invoice" on _invoice_pay_days]
 *   carried in-full, revert card [forty-five; un-strike " of invoice"], the zone "in full" / "of invoice"
 *   kept in-full -> []: our earlier deletion of " of invoice" stands with its own date
 */
function revertsOfCarriedCards(carried: LiveReviewBulkApplyRemedy[], outcomesByParagraph: ZonedParagraphOutcomes[]): RedlineTextRevert[] {
    const revertKeys = new Set<string>();
    /**
     * CASES, for each zone on the paragraph the revert names
     *   - the card offered no option in it -> it does not hold the card's words
     *   - the choice kept the card there   -> the card's words stand
     *   - it went to another reading       -> the revert is left out
     */
    const zonesOnRevertedParagraphKeptCard = (remedy: LiveReviewBulkApplyRemedy, revert: RedlineTextRevert): boolean =>
        outcomesByParagraph
            .filter(({ zonedParagraph }) => zonedParagraph.bookmarkName === revert.bookmark)
            .every(({ zoneOutcomes }) => zoneOutcomes.every((outcome) =>
                !outcome.chosenZoneText.zone.options.some((option) => option.cardId === remedy.rule_uuid) || outcome.keptCardIds.includes(remedy.rule_uuid)));
    const revertsWhoseWordsStand = carried.flatMap((remedy) => (remedy.target_texts?.reverts ?? []).filter((revert) => zonesOnRevertedParagraphKeptCard(remedy, revert)));
    return revertsWhoseWordsStand.filter((revert) => {
        const revertKey = JSON.stringify([revert.bookmark, revert.changeId, revert.expectedChange, revert.text]);
        /** WHEN an earlier carried card reverts this same change CONSEQUENCE it is left out. */
        if (revertKeys.has(revertKey)) return false;
        revertKeys.add(revertKey);
        return true;
    });
}

/**
 * The part of a carried zoned card a zone choice gave to another reading, as the
 * card shows it: Applied, part superseded by the cards kept in its place.
 *
 * CASES
 *   - the card was kept in every zone it is in                  -> none: it landed whole
 *   - it lost a zone because the request for it failed           -> none: that part failed, so
 *                                                                   the run-end check judges the
 *                                                                   card whole and reports it
 *   - it lost zones the choice settled                           -> the zones' reasons and the
 *                                                                   cards kept there; the page's
 *                                                                   own reading names no card
 *
 * EXAMPLE
 *   card A: "thirty (30)" -> "forty-five (45)" and "receipt" -> "invoice"
 *   zone "thirty (30)" keeps card B's "sixty (60)"; A's "invoice" lands uncontested
 *   -> [{ A, "One term only.", conflicting [B] }]
 */
function partSupersessionOf(remedy: LiveReviewBulkApplyRemedy, zonesOfThisCard: ZoneOutcome[]): DroppedCard[] {
    const lostZones = zonesOfThisCard.filter((outcome) => !outcome.keptCardIds.includes(remedy.rule_uuid));
    /**
     * WHEN the card lost no zone, or lost one only because the request failed
     *   CONSEQUENCE
     *     nothing is superseded: the card landed whole, or its missing part failed and the
     *     run-end check reports it against the card as proposed.
     */
    if (lostZones.length === 0 || lostZones.some((outcome) => outcome.requestFailed)) return [];
    return [{
        ruleId: remedy.rule_uuid,
        reason: Array.from(new Set(lostZones.map((outcome) => outcome.reason).filter((reason) => reason.length > 0))).join(" ") || NO_VERDICT_REASON,
        conflictingRuleIds: Array.from(new Set(lostZones.flatMap((outcome) => outcome.keptCardIds))),
    }];
}

/**
 * A partly superseded card as the run wrote it: the composed paragraphs it changes,
 * read as the choice left them, with its own insertions and the table changes the
 * run kept for it. The run-end check judges the card on this, so the part the
 * choice gave away is never read as a change that failed to land.
 *
 * EXAMPLE
 *   card A lists PAY and TERM; the composed card holds PAY, TERM and FEE
 *   -> PAY and TERM as composed, A's insertions, A's kept table changes
 *
 * FALLBACK
 *   `remedy` is undefined only if the card is not carried, which the caller never
 *   passes; the composed card is then returned whole.
 */
function cardAsWrittenFor(remedy: LiveReviewBulkApplyRemedy | undefined, composedCard: RedlineTextProposal, keptRowDeletionIds: Set<string>): RedlineTextProposal {
    const ownCard = remedy?.target_texts ?? null;
    if (remedy === undefined || ownCard === null) return composedCard;
    const ownBookmarks = new Set(ownCard.paragraphs.flatMap((cardParagraph) => (cardParagraph.bookmark === null ? [] : [cardParagraph.bookmark])));
    const ownTableChanges = tableChangesOf([remedy], keptRowDeletionIds);
    return {
        ...composedCard,
        paragraphs: composedCard.paragraphs.filter((composedParagraph) => composedParagraph.bookmark !== null && ownBookmarks.has(composedParagraph.bookmark)),
        insertions: ownCard.insertions,
        tableChanges: ownTableChanges,
        cardKind: cardKindOfTableChanges(ownTableChanges),
    };
}

/**
 * The comment as the panel's comment step writes it; `target_text` is empty as the server leaves it.
 *
 * NULL
 *   `waits_for_rule_uuid` is null for a comment that stands alone; a card's own comment, or a
 *   comment card held on a redline, names the card it waits for.
 */
const commentEntryOf = (comment: LiveReviewBulkApplyComment): BulkApplyCommentEntry => ({
    target_bookmark_names: [comment.bookmark],
    comment_text: comment.text,
    target_text: "",
    placement: comment.placement,
    anchor_text: comment.anchor_text,
    rule_uuids: [comment.rule_uuid],
    rule_names: [comment.rule_name],
    issue_comment_id: comment.issue_comment_id,
    waits_for_rule_uuid: comment.waits_for_rule_uuid ?? null,
});

/**
 * A core request's answer, or the sentence its cards show when the request failed
 * (a transport error, an error status, an answer that does not parse).
 */
type CoreAnswer<Answer> = { kind: "answered"; answer: Answer } | { kind: "failed"; reason: string };

/**
 * The core's answer to one request of the run, never thrown: a failed request is
 * reported, and only the cards that waited on it fail.
 */
async function coreAnswerOf<Answer>(ask: () => Promise<Answer>, waitingRuleIds: string[], stage: string): Promise<CoreAnswer<Answer>> {
    try {
        return { kind: "answered", answer: await ask() };
    } catch (failure) {
        reportFoldFailure(failure, waitingRuleIds, stage);
        return { kind: "failed", reason: foldFailureReasonOf(failure) };
    }
}

/** The answer of a request the run did not need to make: no group waited on it. */
const nothingAsked = <Answer>(): Promise<CoreAnswer<Answer[]>> => Promise.resolve({ kind: "answered", answer: [] });

/** The cards the fold could not lay as the writer takes them: one empty entry, which the writer reports as their rules failing, and each card with its reason. */
function failedCardsEntriesOf(failedCards: FailedCard[]): GroupEntries {
    /** WHEN every card of the group was laid CONSEQUENCE no entry: an entry naming no rule would write nothing and report nothing. */
    if (failedCards.length === 0) return { mergeEntries: [], tableCards: [], droppedCards: [], notChosenCards: [], ...NO_CARD_RESHAPED_BY_A_CHOICE };
    const failedRemedies = failedCards.map((failedCard) => failedCard.remedy);
    return {
        mergeEntries: [mergeEntryOf(bookmarkNamesOf(failedRemedies), failedRemedies, [], false)],
        tableCards: [],
        droppedCards: failedCards.map((failedCard) => ({ ruleId: failedCard.remedy.rule_uuid, reason: failedCard.reason, conflictingRuleIds: [] })),
        notChosenCards: [],
        ...NO_CARD_RESHAPED_BY_A_CHOICE,
    };
}

/**
 * True when a card of a group whose paragraphs are all gone is done: it only deletes
 * them (`redlineWritesNothingWhereItsParagraphsAreGone`), and the user already did.
 *
 * EXAMPLE
 *   card deleting 7.2 whole, 7.2 deleted by the user   -> true: credited, nothing written
 *   card rewriting 7.2, 7.2 deleted by the user         -> false: recovery found no place for it
 */
const cardIsDoneWhereItsParagraphsAreGone = (remedy: LiveReviewBulkApplyRemedy): boolean =>
    remedy.target_texts !== null && redlineWritesNothingWhereItsParagraphsAreGone(textsPayloadFrom(remedy.target_texts));

/**
 * The cards the run credits without writing anything: every card of a group whose
 * paragraphs are all gone that only deleted them (`cardIsDoneWhereItsParagraphsAreGone`).
 *
 * WHY
 *   The user already made the deletion such a card asks for; it reads Applied, as the
 *   server pipeline's "already applied" changes do, never "We cannot find where to
 *   apply this" (live run of #15, step 4, 2026-10-18).
 */
function alreadyAppliedRuleIdsOf(outcomes: GroupOutcome[]): string[] {
    return outcomes.flatMap((outcome) => (outcome.kind === "lost" ? outcome.group.filter(cardIsDoneWhereItsParagraphsAreGone).map((remedy) => remedy.rule_uuid) : []));
}

/**
 * The entries of one folded group, once the core has answered.
 *
 * CASES
 *   - folded    -> one merge entry carrying every card of it
 *   - zoned     -> its paragraphs composed as chosen (`composedZonedGroupEntries`)
 *   - lost      -> an empty entry, every card dropped with the lost-location sentence
 *   - failed    -> nothing of its own: every card is in its failed cards
 */
function entriesOfOutcome(outcome: GroupOutcome, zoneChoices: CoreAnswer<ParagraphZoneChoices[]>): GroupEntries {
    if (outcome.kind === "folded") return { mergeEntries: [mergeEntryOf(bookmarkNamesOf(outcome.group), outcome.group, outcome.packageOoxmls, outcome.wordsExportAsGiven)], tableCards: [], droppedCards: [], notChosenCards: [], ...NO_CARD_RESHAPED_BY_A_CHOICE };
    /**
     * CASES, for a zoned group
     *   - the zone choice was answered -> its choices, found by paragraph
     *   - the request failed           -> no choice, and the failure as the reason for every card that waited on it
     */
    if (outcome.kind === "zoned") return zoneChoices.kind === "answered" ? composedZonedGroupEntries(outcome, zoneChoices.answer, null) : composedZonedGroupEntries(outcome, [], zoneChoices.reason);
    if (outcome.kind === "lost") {
        const unlocatedCards = outcome.group.filter((remedy) => !cardIsDoneWhereItsParagraphsAreGone(remedy));
        /**
         * CASES
         *   - every card only deletes the paragraphs the user already deleted -> no entry: they are done
         *     (`alreadyAppliedRuleIdsOf`)
         *   - otherwise -> an entry with no package for the others, so the writer reports their rules
         *     failed while each dropped card carries the lost-location sentence
         */
        return {
            mergeEntries: unlocatedCards.length === 0 ? [] : [mergeEntryOf(bookmarkNamesOf(unlocatedCards), unlocatedCards, [], false)],
            tableCards: [],
            droppedCards: unlocatedCards.map((remedy) => ({ ruleId: remedy.rule_uuid, reason: CANNOT_LOCATE_TARGET_MESSAGE, conflictingRuleIds: [] })),
            notChosenCards: [],
            ...NO_CARD_RESHAPED_BY_A_CHOICE,
        };
    }
    return { mergeEntries: [], tableCards: [], droppedCards: [], notChosenCards: [], ...NO_CARD_RESHAPED_BY_A_CHOICE };
}

/**
 * The entries of one group, with its failed cards after them. A group whose
 * entries cannot be built (the composed paragraph or the core's answer does not
 * build) drops its own cards with the failure; every other group is untouched.
 */
function entriesOfGroup(outcome: GroupOutcome, zoneChoices: CoreAnswer<ParagraphZoneChoices[]>): GroupEntries {
    const failedCardsEntries = failedCardsEntriesOf(outcome.failedCards);
    let entries: GroupEntries;
    try {
        entries = entriesOfOutcome(outcome, zoneChoices);
    } catch (failure) {
        /** WHEN the group's entries do not build CONSEQUENCE every card the group still held is dropped with the failure; no other group is touched. */
        entries = failedCardsEntriesOf(failedGroupOf(outcome.group, failure, "group_entries").failedCards);
    }
    return {
        mergeEntries: [...entries.mergeEntries, ...failedCardsEntries.mergeEntries],
        tableCards: entries.tableCards,
        droppedCards: [...entries.droppedCards, ...failedCardsEntries.droppedCards],
        notChosenCards: entries.notChosenCards,
        partlySupersededCards: entries.partlySupersededCards,
        cardsAsWritten: entries.cardsAsWritten,
    };
}

/**
 * The line the conflict step shows: how many conflicts the core resolved, and how
 * many it could not, because its answer came back short or its request failed.
 *
 * CASES
 *   - no conflict        -> "No conflicts"
 *   - every one resolved -> "1 conflict resolved", "3 conflicts resolved"
 *   - none resolved      -> "2 conflicts could not be resolved"
 *   - some of each       -> "1 conflict resolved, 1 could not be resolved"
 *
 * EXAMPLE
 *   conflictCount 2, resolvedCount 1 -> 2 - 1 = 1 unresolved -> "1 conflict resolved, 1 could not be resolved"
 */
function conflictStepLineOf(conflictCount: number, resolvedCount: number): string {
    const unresolvedCount = conflictCount - resolvedCount;
    /** "conflict" for one, "conflicts" for any other count. */
    const conflictsWord = (count: number): string => (count === 1 ? "conflict" : "conflicts");
    if (conflictCount === 0) return "No conflicts";
    if (unresolvedCount === 0) return `${resolvedCount} ${conflictsWord(resolvedCount)} resolved`;
    if (resolvedCount === 0) return `${unresolvedCount} ${conflictsWord(unresolvedCount)} could not be resolved`;
    return `${resolvedCount} ${conflictsWord(resolvedCount)} resolved, ${unresolvedCount} could not be resolved`;
}

/**
 * The zone choice requests of every paragraph of a zoned group; a group whose
 * requests cannot be read fails alone.
 */
function zoneChoiceRequestsOfGroup(zonedGroup: ZonedGroup): ConflictParagraphRequest[] {
    return zonedGroup.zonedParagraphs.flatMap((zonedParagraph) => {
        const request = zoneChoiceRequestOf(zonedParagraph, zonedGroup.group);
        /** A paragraph whose every zone settled alone asks nothing. */
        return request === null ? [] : [request];
    });
}

/**
 * The cards the run writes that are partly applied once their write lands, each
 * with the sentence naming what of it went differently, read off the page its
 * group was folded on: a card is never refused for a paragraph or cell the page
 * does not hold.
 *
 * CASES, per written card of a group whose page was read, for its paragraphs the page does not hold
 *   - written by the merge writer (body paragraphs)
 *       - one writes words (`paragraphIsWrittenWhereGone`) -> it was written back where the user deleted it:
 *                                                            named by the text the user deleted
 *       - each only deleted words, or none changes         -> nothing to report: the deletion is done
 *   - written by the applier (a table card)
 *       - a cell the document no longer holds, which the
 *         card changes                                     -> not written, the user removed its cell or row
 *       - a cell Word still holds but did not return,
 *         which the card changes                           -> not written: Word did not return it
 *       - a cell the card leaves as it is, or one the
 *         document no longer holds which the card empties  -> nothing to report
 *   - a card written as proposed, or not written at all    -> none
 *
 * WHY a table card's missing cell is not written back
 *   A cell stands in its row; text written back would land in a neighbour's cell.
 *   The cells on the page are written and the card says which one was not.
 *
 * EXAMPLE
 *   card "both" [_pay, _term "The term is one year." -> "The term is two years."], page holds _pay alone
 *   -> [{ both, "“The term is one year.” was deleted from the document; …", [] }]
 *   table card "rewrite" over rows 0-2, Word returned row 0 alone, rows 1-2 still in the document
 *   -> [{ rewrite, "Word did not return “NARROW: When did they sign it?”, …", [] }]
 */
function partlyAppliedCardsOf(groupPageReads: GroupPageRead[], mergeEntries: BulkApplyMergeEntry[], tableCards: TableCardEntry[]): PartlyAppliedCard[] {
    const mergedRuleIds = new Set(mergeEntries.filter((entry) => entry.target_ooxml_after.length > 0).flatMap((entry) => entry.rule_uuids));
    const tableCardRuleIds = new Set(tableCards.flatMap((entry) => entry.rule_uuids));
    return groupPageReads.flatMap((groupPageRead) => {
        /** WHEN the group's page was not read, or is gone CONSEQUENCE none of its cards was written. */
        if (groupPageRead.kind !== "read" || groupPageRead.pageRead === null) return [];
        const { bookmarkNamesNotFoundInDocument } = groupPageRead.pageRead;
        const { pageParagraphs } = groupPageRead;
        const bookmarkNamesOnPage = new Set(pageParagraphs.flatMap((pageParagraph) => pageParagraph.bookmarkNames));
        const cellPresenceOnPage: CardCellPresence = {
            cellIsOnPage: (bookmarkName) => bookmarkNamesOnPage.has(bookmarkName),
            cellIsGone: (bookmarkName) => bookmarkNamesNotFoundInDocument?.has(bookmarkName) ?? false,
        };
        return groupPageRead.group.flatMap((remedy): PartlyAppliedCard[] => {
            const card = remedy.target_texts;
            /** WHEN the card is markup CONSEQUENCE it names no paragraph to miss. */
            if (card === null) return [];
            const paragraphsMissingFromPage = Array.from(cardParagraphsGoneFromRunning(card, pageParagraphs).values()).flat();
            /**
             * CASES
             *   - the merge writer wrote it -> the paragraphs written back where the user deleted them:
             *                                  every write of it landed
             *   - the applier wrote it      -> the cells it did not write: some of it did not land
             *   - neither                   -> none: the card was dropped or not chosen
             */
            if (mergedRuleIds.has(remedy.rule_uuid)) {
                const writtenBackReason = writtenBackReasonOrNullOf(paragraphsMissingFromPage, writtenPackagesOfRule(mergeEntries, remedy.rule_uuid));
                return writtenBackReason === null ? [] : [{ ruleId: remedy.rule_uuid, reason: writtenBackReason, conflictingRuleIds: [], everyWriteLanded: true, writtenBack: true }];
            }
            const cellsNotWrittenReason = tableCardRuleIds.has(remedy.rule_uuid) ? cardCellsNotWrittenReasonOrNullOf(card.paragraphs, cellPresenceOnPage) : null;
            return cellsNotWrittenReason === null ? [] : [{ ruleId: remedy.rule_uuid, reason: cellsNotWrittenReason, conflictingRuleIds: [], everyWriteLanded: false }];
        });
    });
}

/** Every package the merge entries write for the card's rule. */
const writtenPackagesOfRule = (mergeEntries: BulkApplyMergeEntry[], ruleId: string): string[] =>
    mergeEntries.filter((entry) => entry.rule_uuids.includes(ruleId)).flatMap((entry) => entry.target_ooxml_after);

/**
 * The sentence naming the deleted paragraphs a body card wrote back: the ones whose
 * written paragraph holds the card's words.
 *
 * CASES, per missing paragraph
 *   - it only deleted words the user already deleted, or changes none -> not named: nothing went back
 *   - the written package holds it, reading as the card proposed
 *     (`cardLandingOnParagraphText`)                                   -> named: this card's words went back
 *   - another card's words went back in its place (the conflict
 *     choice kept them), or none did                                   -> not named: the choice reports this
 *                                                                         card as not chosen or partly superseded
 *
 * EXAMPLE
 *   x [_term -> "The term is two years."], y [_term -> "The term is three years."], the choice kept x
 *   -> x names "The term is one year."; y names nothing
 *
 * NULL
 *   no paragraph of this card was written back.
 */
function writtenBackReasonOrNullOf(paragraphsMissingFromPage: RedlineTextParagraph[], writtenPackageOoxmls: string[]): string | null {
    const writingParagraphs = paragraphsMissingFromPage.filter((missingParagraph) => paragraphIsWrittenWhereGone(missingParagraph.textWhenProposed, missingParagraph.textProposed));
    /** WHEN nothing of the card goes back CONSEQUENCE no package is read. */
    if (writingParagraphs.length === 0) return null;
    const writtenTextByBookmark = new Map(writtenPackageOoxmls.flatMap((packageOoxml) =>
        Array.from(new OoxmlRange(packageOoxml).paragraphElementsByBookmarkName(), ([bookmarkName, paragraphElement]) => [bookmarkName, new ParagraphModel(paragraphElement).text] as const)));
    const cardWordsWentBack = (missingParagraph: RedlineTextParagraph): boolean => {
        const writtenText = missingParagraph.bookmark === null ? undefined : writtenTextByBookmark.get(missingParagraph.bookmark);
        return writtenText !== undefined && cardLandingOnParagraphText(missingParagraph, writtenText) === "applied";
    };
    const deletedParagraphTexts = writingParagraphs.filter(cardWordsWentBack).map((missingParagraph) => missingParagraph.textWhenProposed);
    return deletedParagraphTexts.length === 0 ? null : writtenBackReasonOf(deletedParagraphTexts);
}

/** One card recovery placed beside a paragraph, as the applier writes it: alone, as it was proposed. */
const besideParagraphCardOf = (remedy: LiveReviewBulkApplyRemedy, card: RedlineTextProposal): TableCardEntry => ({
    rule_uuids: [remedy.rule_uuid],
    rule_names: [remedy.rule_name],
    target_bookmark_names: remedy.target_bookmark_names,
    card,
    pageOfTheRun: null,
});

/**
 * Cards recovery placed beside one paragraph that write back the same deleted
 * paragraphs, zoned as those paragraphs (`deletedParagraphsZonedByPageIndexBefore`
 * over no page: every paragraph of theirs is gone), so the conflict choice decides
 * whose words go back, and the applier writes one composed card beside the paragraph.
 *
 * WHY the applier writes the group
 *   The cards' bookmarks sit on the neighbour recovery placed them beside, so no page
 *   holds their paragraphs; the applier writes a card so placed as new paragraphs beside
 *   that neighbour (`RedlineSource.placedFor`). `groupWritesIntoTable` is true to send the
 *   composed card to it, as a table group's is.
 *
 * EXAMPLE
 *   x [_term -> "The term is two years."], y [_term -> "The term is three years."], 8.1 deleted,
 *   both placed after 7.9
 *   -> one zoned paragraph _term, base "The term is one year.", zone "two" / "three"
 *   -> the choice keeps x: one card writing "The term is two years." after 7.9, carrying x
 */
function zonedGroupOfCardsPlacedBeside(group: LiveReviewBulkApplyRemedy[]): ZonedGroup {
    return {
        kind: "zoned",
        group,
        pagePackageOoxml: OoxmlRange.packageAround(""),
        wordsExportAsGiven: false,
        groupWritesIntoTable: true,
        rowPlacement: pageRowPlacementOf([], undefined),
        zonedParagraphs: Array.from(deletedParagraphsZonedByPageIndexBefore(group, []).values()).flat(),
        failedCards: [],
    };
}

/**
 * The cards recovery placed beside a paragraph, in the shape the run writes them.
 *
 * CASES, per set of cards that share a bookmark
 *   - one card                       -> written alone, as proposed (`besideParagraphCardOf`)
 *   - several                        -> zoned together (`zonedGroupOfCardsPlacedBeside`), so they are
 *                                       chosen between before one composed card is written
 *   - their zoning cannot be read    -> the set fails alone, with the reason
 */
interface CardsPlacedBeside {
    writtenAlone: TableCardEntry[];
    zonedOrFailed: GroupOutcome[];
}

function cardsPlacedBesideOf(remediesPlacedBeside: LiveReviewBulkApplyRemedy[]): CardsPlacedBeside {
    const writtenAlone: TableCardEntry[] = [];
    const zonedOrFailed: GroupOutcome[] = [];
    for (const group of groupRemediesSharingABookmark(remediesPlacedBeside)) {
        const loneCard = group.length === 1 ? group[0].target_texts : null;
        /** WHEN the set is one text card CONSEQUENCE it is written as proposed: there is no one to choose against. */
        if (loneCard !== null) {
            writtenAlone.push(besideParagraphCardOf(group[0], loneCard));
            continue;
        }
        try {
            zonedOrFailed.push(zonedGroupOfCardsPlacedBeside(group));
        } catch (failure) {
            zonedOrFailed.push(failedGroupOf(group, failure, "zone_cards_placed_beside"));
        }
    }
    return { writtenAlone, zonedOrFailed };
}

/**
 * The run's entries: every group folded in parallel, the zones of colliding cards
 * chosen by the core in one request, one merge entry per group carrying every
 * member's rule ids, the table groups' cards for the applier, and the comments as
 * they came.
 *
 * WHY one card, one group or one request never fails the run
 *   Each group folds on its own: a page that cannot be read, a card whose data does
 *   not lay, or entries that do not build drop that group's or that card's rules
 *   with the reason, and every other group is written. A core request that fails
 *   drops the cards that waited on its answer.
 */
export async function localBulkApplyEntries(input: LocalBulkApplyInput, report: LocalFoldReporter = SILENT_FOLD_REPORTER): Promise<LocalBulkApplyEntries> {
    const groups = groupRemediesSharingABookmark(input.remedies);
    report.stepStarted("checking_conflicts");
    const coveredSpansOver = input.coveredSpansOver ?? SPANS_OF_A_TRACKER_THAT_HAS_SEEN_NOTHING;
    const groupsWithUnfoundRevertsTakenOut = (await groupsJoinedOnSharedParagraphs(await Promise.all(groups.map((group) => pageReadOfGroup(group, input))), input))
        .map((groupPageRead) => groupWithUnfoundRevertsTakenOut(groupReadOverCoveredSpans(groupPageRead, coveredSpansOver)));
    const groupPageReads = groupsWithUnfoundRevertsTakenOut.map(({ groupPageRead }) => groupPageRead);
    const partlyWrittenCards = groupsWithUnfoundRevertsTakenOut.flatMap((takenOut) => takenOut.partlyWrittenCards);
    /** Each group folded, the cards left with nothing to write once their unfound changes were taken out listed with its own failed cards. */
    const folded = groupsWithUnfoundRevertsTakenOut.map(({ groupPageRead, cardsNotWritten }): GroupOutcome => {
        const outcome = foldGroupOntoPage(groupPageRead);
        return { ...outcome, failedCards: [...cardsNotWritten, ...outcome.failedCards] };
    });
    const cardsPlacedBeside = cardsPlacedBesideOf(input.remediesPlacedBesideParagraphs ?? []);
    const zoneChoiceRequestsOf = new Map<ZonedGroup, ConflictParagraphRequest[]>();
    const withZoneChoiceRequests = (outcome: GroupOutcome): GroupOutcome => {
        /** Only a zoned group asks the zone choice anything. */
        if (outcome.kind !== "zoned") return outcome;
        try {
            zoneChoiceRequestsOf.set(outcome, zoneChoiceRequestsOfGroup(outcome));
            return outcome;
        } catch (failure) {
            /** WHEN the group's questions cannot be written CONSEQUENCE the group fails alone, its earlier failed cards kept. */
            const failedGroup = failedGroupOf(outcome.group, failure, "zone_choice_requests");
            return { ...failedGroup, failedCards: [...outcome.failedCards, ...failedGroup.failedCards] };
        }
    };
    const outcomes = folded.map(withZoneChoiceRequests);
    const besideOutcomes = cardsPlacedBeside.zonedOrFailed.map(withZoneChoiceRequests);
    const zonedGroups = [...outcomes, ...besideOutcomes].filter((result): result is ZonedGroup => result.kind === "zoned");
    /**
     * The conflicts the panel counts: every zone a choice is needed for. A zone the
     * add-in settles alone (the same change twice, overlapping deletions) is no conflict.
     *
     * EXAMPLE
     *   3.7: zone "initial" first vs original (open), zone "two (2)" three vs three (settled) -> 1 conflict
     */
    const openZoneCountOf = (zonedParagraph: ZonedParagraph): number => zonedParagraph.zoning.zones.filter((zone) => settledTextOf(zone, zonedParagraph) === null).length;
    const conflictCount = zonedGroups.reduce((zoneTotal, zonedGroup) => zoneTotal + zonedGroup.zonedParagraphs.reduce((paragraphTotal, zonedParagraph) => paragraphTotal + openZoneCountOf(zonedParagraph), 0), 0);
    const zoneChoiceRequests = zonedGroups.flatMap((zonedGroup) => zoneChoiceRequestsOf.get(zonedGroup) ?? []);
    const ruleIdsOf = (waitingGroups: GroupOutcome[]): string[] => waitingGroups.flatMap((waitingGroup) => waitingGroup.group.map((remedy) => remedy.rule_uuid));
    const zoneChoices = zoneChoiceRequests.length > 0 ? await coreAnswerOf(() => input.chooseConflictOptions(zoneChoiceRequests), ruleIdsOf(zonedGroups), "choose_conflict_options") : await nothingAsked<ParagraphZoneChoices>();
    /**
     * The conflicts the core answered: every open zone its zone choice named. A zone
     * left out of an answer that came back short, or of a request that failed, was not
     * resolved.
     */
    const answeredZoneCountOf = (zonedParagraph: ZonedParagraph): number => {
        const paragraphChoices = zoneChoices.kind === "answered" ? zoneChoices.answer.find((choices) => choices.paragraph_label === zonedParagraph.bookmarkName) : undefined;
        return zonedParagraph.zoning.zones.filter((zone, zoneIndex) =>
            settledTextOf(zone, zonedParagraph) === null && (paragraphChoices?.zones.some((choice) => choice.zone_label === zoneLabelAt(zoneIndex)) ?? false)).length;
    };
    const resolvedCount = zonedGroups.reduce((zoneTotal, zonedGroup) => zoneTotal + zonedGroup.zonedParagraphs.reduce((paragraphTotal, zonedParagraph) => paragraphTotal + answeredZoneCountOf(zonedParagraph), 0), 0);
    report.stepFinished("checking_conflicts", conflictStepLineOf(conflictCount, resolvedCount));
    const groupEntries = outcomes.map((outcome) => entriesOfGroup(outcome, zoneChoices));
    const besideEntries = besideOutcomes.map((outcome) => entriesOfGroup(outcome, zoneChoices));
    const everyGroupEntries = [...groupEntries, ...besideEntries];
    const mergeData = everyGroupEntries.flatMap((entries) => entries.mergeEntries);
    const tableCards = groupEntries.flatMap((entries) => entries.tableCards);
    const droppedCards = everyGroupEntries.flatMap((entries) => entries.droppedCards);
    /**
     * The cards written with a change of theirs taken out, less the ones the run then dropped.
     *
     * WHY the dropped ones are left out
     *   A card whose other paragraphs failed to build wrote nothing. Reported partly applied it would
     *   also be reported failed, and a partly applied card loses its Apply button: the user could not
     *   retry a card that landed nothing.
     *
     * EXAMPLE
     *   the Cap Damages card, its cap revert gone, its exceptions paragraph's build throwing
     *   -> dropped with the build's error; not partly applied, not as written
     */
    const partlyWrittenCardsLanded = partlyWrittenCards.filter(({ partlyApplied }) => !droppedCards.some((dropped) => dropped.ruleId === partlyApplied.ruleId));
    return {
        mergeData,
        partlyAppliedCards: [...partlyAppliedCardsOf(groupPageReads, mergeData, tableCards), ...partlyWrittenCardsLanded.map(({ partlyApplied }) => partlyApplied)],
        alreadyAppliedRuleIds: alreadyAppliedRuleIdsOf(outcomes),
        commentData: input.comments.map(commentEntryOf),
        tableCards,
        besideParagraphCards: [...cardsPlacedBeside.writtenAlone, ...besideEntries.flatMap((entries) => entries.tableCards)],
        droppedCards,
        notChosenCards: everyGroupEntries.flatMap((entries) => entries.notChosenCards),
        partlySupersededCards: everyGroupEntries.flatMap((entries) => entries.partlySupersededCards),
        cardsAsWritten: [...everyGroupEntries.flatMap((entries) => entries.cardsAsWritten), ...partlyWrittenCardsLanded.map(({ asWritten }) => asWritten)],
    };
}
